/* global AbortController, Blob, DOMException, HTMLButtonElement, MediaDeviceInfo, MediaRecorder, MediaStream, Response, URLSearchParams */
import {
  AudioWaveform,
  Circle,
  ChevronDown,
  ChevronUp,
  Download,
  FolderOpen,
  FileJson,
  Gauge,
  Maximize2,
  MapPin,
  Menu,
  Mic,
  Music2,
  PanelLeft,
  Pause,
  Play,
  Plus,
  Redo2,
  Repeat2,
  RefreshCw,
  Route,
  Save,
  Scissors,
  Settings as SettingsIcon,
  SlidersVertical,
  SkipBack,
  SkipForward,
  Square,
  StretchHorizontal,
  StretchVertical,
  Trash2,
  TimerReset,
  Undo2,
  Upload,
  Zap,
  ZoomIn,
  ZoomOut,
} from "../icons";
import { useConvexAuth } from "@convex-dev/auth/react";
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ChangeEventHandler, type MouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { AppearanceProvider, useAppearance } from "../appearance";
import { IconProvider } from "../icons";
import { analyzeAudio } from "../lib/audioMeta";
import AppearanceSettings from "./AppearanceSettings";
import "./Daw.css";

type TrackType = "audio" | "drum" | "midi" | "bus";
type ParamValue = string | number | boolean;

type DawClip = {
  id: string;
  file: string;
  start: number;
  offset?: number;
  length?: number;
  fadeIn?: number;
  fadeOut?: number;
};

type AutomationMode = "Read" | "Touch" | "Latch" | "Write";

type DawNote = {
  note: number;
  start: number;
  duration: number;
  velocity: number;
};

type DawTrack = {
  id: string;
  name: string;
  type: TrackType;
  color?: string;
  gain: number;
  pan: number;
  muted: boolean;
  solo: boolean;
  recordArmed: boolean;
  inputMonitoring: boolean;
  hidden: boolean;
  automationMode?: AutomationMode;
  bus?: string;
  kit?: string;
  clips?: DawClip[];
  midiClips?: Array<{ id: string; start: number; length?: number; notes: DawNote[] }>;
  pattern?: { startBar: number; grid: string; lengthBars: number; hits: Array<{ bar: number; beat: number; velocity: number }> };
};

type DawMarker = {
  id: string;
  name: string;
  time: number;
};

type DawArrangementSection = {
  id: string;
  name: string;
  start: number;
  length: number;
};

type DawPlugin = {
  id: string;
  track: string;
  name: string;
  format: "AU" | "VST3";
  path?: string;
  params: Record<string, ParamValue>;
  enabled: boolean;
};

type InstalledPlugin = {
  id: string;
  name: string;
  descriptiveName: string;
  format: string;
  path: string;
  manufacturer: string;
  version: string;
  category: string;
  isInstrument: boolean;
  inputs: number;
  outputs: number;
};

type NativePluginParameter = {
  id: string;
  name: string;
  label: string;
  value: number;
  defaultValue: number;
  numSteps: number;
  discrete: boolean;
  boolean: boolean;
  automatable: boolean;
  text: string;
};

type NativePluginInfo = InstalledPlugin & {
  hasEditor: boolean;
  parameters: NativePluginParameter[];
  parameterCount: number;
};

type NativeTransport = {
  state: "playing" | "stopped" | "error";
  position: number;
  duration: number;
  error?: string | null;
  metronome?: boolean;
  loopStart?: number;
  loopEnd?: number;
  meters?: { master: number; tracks: number[] };
};

type RenderFormat = "wav16" | "wav24" | "wav32";
type RenderSampleRate = 44100 | 48000 | 88200 | 96000;

type DawBounceResult = {
  id: string;
  outputUrl: string;
  fileName: string;
  format: RenderFormat;
  sampleRate: RenderSampleRate;
  bitDepth: 16 | 24 | 32;
  channels: number;
  durationSeconds: number;
  manifestHash?: string;
};

type DawRenderResponse = {
  ok?: boolean;
  id?: string;
  outputUrl?: string;
  fileName?: string;
  message?: string;
  result?: {
    data?: Record<string, unknown>;
    manifest?: { manifestHash?: string };
  };
};

type DawProject = {
  version: 2;
  name: string;
  tempo: number;
  key: string;
  timesig: string;
  markers: DawMarker[];
  arrangement: DawArrangementSection[];
  tracks: DawTrack[];
  fx: Array<{ id: string; track: string; type: string; params: Record<string, ParamValue> }>;
  sends: Array<{ id: string; from: string; to: string; amount: number }>;
  envelopes: Array<{ id: string; track: string; param: string; points: Array<{ time: string; value: number }> }>;
  plugins: DawPlugin[];
  master: {
    gain: number;
    limiter: boolean;
    ceilingDb: number;
    fx: Array<{ id: string; track: string; type: string; params: Record<string, ParamValue> }>;
    plugins: DawPlugin[];
  };
};

type EngineDawTrack = Omit<DawTrack, "color" | "recordArmed" | "inputMonitoring" | "hidden" | "automationMode">;
type EngineDawProject = Omit<DawProject, "key" | "markers" | "arrangement" | "tracks"> & { tracks: EngineDawTrack[] };

type DriveMiniDawProject = {
  id: string;
  name: string;
  artistName: string;
  projectName: string;
  sourceFolder: string;
  relativePath: string;
  modifiedTime?: string;
  size?: string;
  mediaCount?: number;
  mediaFolders?: string[];
};

type DriveMiniDawMedia = {
  id: string;
  name: string;
  mimeType: string;
  relativePath: string;
  size?: string;
  modifiedTime?: string;
};

type DawWaveform = {
  peaks: number[];
  rms: number[];
  loading?: boolean;
  error?: boolean;
};

type AccessTokenFetcher = (options: { forceRefreshToken: boolean }) => Promise<string | null>;

type DesktopProjectPayload = {
  ok: boolean;
  error?: string;
  fileName?: string;
  project?: unknown;
  media?: Array<{ reference: string; path: string }>;
  missingMedia?: string[];
};

type MiniDawDesktopApi = {
  openProject: () => Promise<DesktopProjectPayload | null>;
  onOpenProject: (callback: (payload: DesktopProjectPayload) => void) => () => void;
  saveRender?: (payload: { url: string; fileName: string }) => Promise<{ ok: boolean; canceled?: boolean; filePath?: string; bytes?: number; error?: string }>;
};

function miniDawDesktopApi(): MiniDawDesktopApi | undefined {
  return (window as typeof window & { miniDawDesktop?: MiniDawDesktopApi }).miniDawDesktop;
}

type PluginParameter = {
  id: string;
  name: string;
  kind: "number" | "boolean" | "choice";
  value: ParamValue;
  minimum?: number;
  maximum?: number;
  step?: number;
  unit?: string;
  choices?: string[];
  displayValue?: string;
  defaultValue?: number;
  automatable?: boolean;
};

const STORAGE_KEY = "johnsonboibeats.admin.mini-daw.project.v1";
const LAST_SAVED_KEY = "johnsonboibeats.admin.mini-daw.last-saved.v1";
const VIEW_STORAGE_KEY = "johnsonboibeats.admin.mini-daw.view.v3";
const MIXER_HEIGHT_STORAGE_KEY = "johnsonboibeats.admin.mini-daw.mixer-height.v2";
const DEFAULT_MIXER_HEIGHT = 300;
const MIN_MIXER_HEIGHT = 150;
const MAX_MIXER_HEIGHT = 520;
const DRIVE_PROJECT_LIST_CACHE_TTL_MS = 30 * 1000;
const TRACK_LABEL_WIDTH_STORAGE_KEY = "johnsonboibeats.admin.mini-daw.track-label-width.v1";
const DEFAULT_TRACK_LABEL_WIDTH = 560;
const MIN_TRACK_LABEL_WIDTH = 150;
const MAX_TRACK_LABEL_WIDTH = 400;
const DEFAULT_TRACK_ROW_HEIGHT = 92;
const FIT_TRACK_ROW_MIN = 24;
const AUTO_FIT_SHORT_TRACK_LIMIT = 8;

function timelineViewportHeight(element: HTMLDivElement | null): number {
  if (!element) return 480;
  const measured = element.getBoundingClientRect().height || element.clientHeight;
  return measured > 0 ? measured : 480;
}

function fittedTimelineRowHeight(viewportHeight: number, trackCount: number, hasArrangement: boolean, showMarkers: boolean): number {
  if (trackCount <= 0) return DEFAULT_TRACK_ROW_HEIGHT;
  const headerHeight = 28;
  const globalLanes = (hasArrangement ? 1 : 0) + (showMarkers ? 1 : 0);
  const globalLaneHeight = 24;
  const breathing = 8;
  const trackBudget = Math.max(0, viewportHeight - headerHeight - globalLanes * globalLaneHeight - breathing);
  // Fit means use the available viewport. Do not cap the row height: a short
  // project with only a few tracks should not leave a large empty lower area.
  return Math.max(FIT_TRACK_ROW_MIN, Math.floor(trackBudget / trackCount));
}
type DawViewKey = "arrangement" | "mixer" | "inserts";
type DawViewState = Record<DawViewKey, boolean>;
type SnapMode = "Smart" | "Bar" | "Beat" | "Off";
type DragMode = "No Overlap" | "Overlap";
const defaultDawViewState: DawViewState = { arrangement: true, mixer: true, inserts: true };
const configuredBridgeUrl = (import.meta.env.VITE_MINI_DAW_BRIDGE_URL as string | undefined)?.trim() ?? "";
const DEFAULT_BRIDGE_URL = "http://127.0.0.1:8787";
function desktopBridgeUrl(): string {
  if (typeof window === "undefined") return "";
  const port = new URLSearchParams(window.location.search).get("miniDawBridgePort");
  if (!port || !/^\d+$/.test(port)) return "";
  const numericPort = Number(port);
  return numericPort > 0 && numericPort < 65536 ? `http://127.0.0.1:${numericPort}` : "";
}
function localBridgeUrl(value: string): string {
  if (!value) return "";
  try {
    const url = new URL(value);
    const loopbackHosts = new Set(["localhost", "127.0.0.1", "[::1]"]);
    if (url.protocol !== "http:" || !loopbackHosts.has(url.hostname)) return "";
    const port = Number(url.port || 80);
    if (!Number.isInteger(port) || port < 1 || port > 65535) return "";
    return url.toString().replace(/\/$/, "");
  } catch {
    return "";
  }
}
const BRIDGE_URL = localBridgeUrl(desktopBridgeUrl()) || localBridgeUrl(configuredBridgeUrl) || DEFAULT_BRIDGE_URL;
const BRIDGE_TOKEN = (import.meta.env.VITE_MINI_DAW_BRIDGE_TOKEN as string | undefined)?.trim() ?? "";

const TRACK_LABELS: Record<TrackType, string> = {
  audio: "Audio",
  drum: "Drums",
  midi: "MIDI",
  bus: "Bus",
};

function makeStarterProject(): DawProject {
  return {
    version: 2,
    name: "Portal production session",
    tempo: 120,
    key: "C",
    timesig: "4/4",
    markers: [],
    arrangement: [],
    tracks: [
      { id: "track_01", name: "Lead Vocal", type: "audio", gain: 0, pan: 0, muted: false, solo: false, recordArmed: false, inputMonitoring: false, hidden: false, automationMode: "Read", clips: [{ id: "clip_01", file: "Lead Vocal.wav", start: 0, length: 16 }] },
      { id: "track_02", name: "Instrumental", type: "audio", gain: -3, pan: 0, muted: false, solo: false, recordArmed: false, inputMonitoring: false, hidden: false, automationMode: "Read", clips: [{ id: "clip_02", file: "Instrumental.wav", start: 0, length: 16 }] },
      { id: "track_03", name: "Drums", type: "drum", gain: -2, pan: 0, muted: false, solo: false, recordArmed: false, inputMonitoring: false, hidden: false, pattern: { startBar: 1, grid: "1/16", lengthBars: 4, hits: [{ bar: 1, beat: 1, velocity: 110 }, { bar: 1, beat: 3, velocity: 100 }, { bar: 2, beat: 1, velocity: 110 }, { bar: 2, beat: 3, velocity: 100 }] } },
      { id: "track_04", name: "Vocal Bus", type: "bus", gain: 0, pan: 0, muted: false, solo: false, recordArmed: false, inputMonitoring: false, hidden: false },
    ],
    fx: [],
    sends: [],
    envelopes: [],
    plugins: [],
    master: { gain: 0, limiter: true, ceilingDb: -1, fx: [], plugins: [] },
  };
}

function cloneProject(project: DawProject): DawProject {
  return JSON.parse(JSON.stringify(project)) as DawProject;
}

/** Remove Portal-only presentation and recording fields before native validation. */
function toEngineProject(project: DawProject): EngineDawProject {
  const next = cloneProject(project) as unknown as EngineDawProject & Record<string, unknown>;
  delete next.key;
  delete next.markers;
  delete next.arrangement;
  next.tracks = next.tracks.map((track) => {
    const engineTrack = { ...track } as EngineDawTrack & Record<string, unknown>;
    delete engineTrack.color;
    delete engineTrack.recordArmed;
    delete engineTrack.inputMonitoring;
    delete engineTrack.hidden;
    delete engineTrack.automationMode;
    if (engineTrack.type !== "audio") delete engineTrack.clips;
    if (engineTrack.type !== "midi") delete engineTrack.midiClips;
    if (engineTrack.type !== "drum") {
      delete engineTrack.kit;
      delete engineTrack.pattern;
    }
    return engineTrack;
  });
  return next;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function normalizeMediaPath(value: string): string {
  return value
    .replace(/\\/g, "/")
    .replace(/^\.\//, "")
    .replace(/^\/+/, "")
    .replace(/\/+/g, "/")
    .trim();
}

function mediaPathName(value: string): string {
  const normalized = normalizeMediaPath(value);
  return normalized.slice(normalized.lastIndexOf("/") + 1).toLocaleLowerCase();
}

function findDriveMediaReference(value: string, media: DriveMiniDawMedia[]): DriveMiniDawMedia | null {
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("/") || /^[A-Za-z]:[\\/]/.test(trimmed)) return null;
  const normalized = normalizeMediaPath(value).toLocaleLowerCase();
  if (!normalized) return null;
  const exact = media.find((item) => normalizeMediaPath(item.relativePath).toLocaleLowerCase() === normalized);
  if (exact) return exact;
  const fileName = mediaPathName(value);
  const byName = media.filter((item) => mediaPathName(item.relativePath) === fileName);
  return byName.length === 1 ? byName[0] : null;
}

function projectMediaReferences(project: DawProject): string[] {
  const paths = new Set<string>();
  for (const track of project.tracks) {
    for (const clip of track.clips ?? []) {
      if (clip.file.trim()) paths.add(clip.file);
    }
    if (track.type === "drum" && track.kit?.trim()) paths.add(track.kit);
  }
  return [...paths];
}

function normalizeProject(value: unknown): DawProject | null {
  if (!isRecord(value) || !Array.isArray(value.tracks)) return null;
  const starter = makeStarterProject();
  const tracks = value.tracks.filter(isRecord).map((raw, index): DawTrack => {
    const type = ["audio", "drum", "midi", "bus"].includes(String(raw.type)) ? raw.type as TrackType : "audio";
    const clips = Array.isArray(raw.clips)
      ? raw.clips.filter(isRecord).map((clip, clipIndex) => ({
          id: stringOr(clip.id, `clip_${String(clipIndex + 1).padStart(2, "0")}`),
          file: stringOr(clip.file, "Audio file"),
          start: Math.max(0, numberOr(clip.start, 0)),
          offset: Math.max(0, numberOr(clip.offset, 0)),
          length: Math.max(0, numberOr(clip.length, 4)),
          fadeIn: Math.max(0, numberOr(clip.fadeIn, 0)),
          fadeOut: Math.max(0, numberOr(clip.fadeOut, 0)),
        }))
      : [];
    const midiClips = Array.isArray(raw.midiClips)
      ? raw.midiClips.filter(isRecord).map((clip, clipIndex) => ({
          id: stringOr(clip.id, `midiclip_${String(clipIndex + 1).padStart(2, "0")}`),
          start: Math.max(0, numberOr(clip.start, 0)),
          length: Math.max(0, numberOr(clip.length, 4)),
          notes: Array.isArray(clip.notes)
            ? clip.notes.filter(isRecord).map((note) => ({
                note: Math.max(0, Math.min(127, Math.round(numberOr(note.note, 60)))),
                start: Math.max(0, numberOr(note.start, 0)),
                duration: Math.max(0.05, numberOr(note.duration, 0.5)),
                velocity: Math.max(1, Math.min(127, Math.round(numberOr(note.velocity, 100)))),
              }))
            : [],
        }))
      : [];
    const normalizedTrack: DawTrack = {
      id: stringOr(raw.id, `track_${String(index + 1).padStart(2, "0")}`),
      name: stringOr(raw.name, `Track ${index + 1}`),
      type,
      color: typeof raw.color === "string" && /^#[0-9a-f]{6}$/i.test(raw.color) ? raw.color : undefined,
      gain: Math.max(-60, Math.min(12, numberOr(raw.gain, 0))),
      pan: Math.max(-1, Math.min(1, numberOr(raw.pan, 0))),
      muted: raw.muted === true,
      solo: raw.solo === true,
      recordArmed: raw.recordArmed === true,
      inputMonitoring: raw.inputMonitoring === true,
      hidden: raw.hidden === true,
      automationMode: ["Read", "Touch", "Latch", "Write"].includes(String(raw.automationMode)) ? raw.automationMode as AutomationMode : "Read",
      bus: typeof raw.bus === "string" ? raw.bus : undefined,
      kit: typeof raw.kit === "string" ? raw.kit : undefined,
      clips,
      midiClips,
      pattern: isRecord(raw.pattern)
        ? {
            startBar: Math.max(1, Math.round(numberOr(raw.pattern.startBar, 1))),
            grid: stringOr(raw.pattern.grid, "1/16"),
            lengthBars: Math.max(1, Math.round(numberOr(raw.pattern.lengthBars, 1))),
            hits: Array.isArray(raw.pattern.hits)
              ? raw.pattern.hits.filter(isRecord).map((hit) => ({
                  bar: Math.max(1, Math.round(numberOr(hit.bar, 1))),
                  beat: Math.max(1, numberOr(hit.beat, 1)),
                  velocity: Math.max(1, Math.min(127, Math.round(numberOr(hit.velocity, 100)))),
                }))
              : [],
          }
        : undefined,
    };
    if (type !== "audio") delete normalizedTrack.clips;
    if (type !== "midi") delete normalizedTrack.midiClips;
    if (type !== "drum") {
      delete normalizedTrack.kit;
      delete normalizedTrack.pattern;
    }
    return normalizedTrack;
  });
  if (tracks.length === 0) tracks.push(...starter.tracks);
  const plugins = Array.isArray(value.plugins)
    ? value.plugins.filter(isRecord).map((plugin, index) => ({
        id: stringOr(plugin.id, `plugin_${String(index + 1).padStart(2, "0")}`),
        track: stringOr(plugin.track, tracks[0]?.id ?? "track_01"),
        name: stringOr(plugin.name, "Plugin"),
        format: plugin.format === "AU" ? "AU" as const : "VST3" as const,
        path: typeof plugin.path === "string" ? plugin.path : undefined,
        params: isRecord(plugin.params) ? Object.fromEntries(Object.entries(plugin.params).filter(([, entry]) => ["string", "number", "boolean"].includes(typeof entry))) as Record<string, ParamValue> : {},
        enabled: plugin.enabled !== false,
      }))
    : [];
  return {
    version: 2,
    name: stringOr(value.name, "Untitled project"),
    tempo: Math.max(20, Math.min(300, numberOr(value.tempo, 120))),
    key: stringOr(value.key, "C"),
    timesig: stringOr(value.timesig, "4/4"),
    markers: Array.isArray(value.markers) ? value.markers.filter(isRecord).map((marker, index) => ({
      id: stringOr(marker.id, `marker_${String(index + 1).padStart(2, "0")}`),
      name: stringOr(marker.name, `Marker ${index + 1}`),
      time: Math.max(0, numberOr(marker.time, 0)),
    })) : [],
    arrangement: Array.isArray(value.arrangement) ? value.arrangement.filter(isRecord).map((section, index) => ({
      id: stringOr(section.id, `section_${String(index + 1).padStart(2, "0")}`),
      name: stringOr(section.name, `Section ${index + 1}`),
      start: Math.max(0, numberOr(section.start, 0)),
      length: Math.max(0.1, numberOr(section.length, 8)),
    })) : [],
    tracks,
    fx: Array.isArray(value.fx) ? value.fx.filter(isRecord).map((fx, index) => ({ id: stringOr(fx.id, `fx_${index + 1}`), track: stringOr(fx.track, tracks[0]?.id ?? ""), type: stringOr(fx.type, "gain"), params: isRecord(fx.params) ? Object.fromEntries(Object.entries(fx.params).filter(([, entry]) => ["string", "number", "boolean"].includes(typeof entry))) as Record<string, ParamValue> : {} })) : [],
    sends: Array.isArray(value.sends) ? value.sends.filter(isRecord).map((send, index) => ({ id: stringOr(send.id, `send_${index + 1}`), from: stringOr(send.from, ""), to: stringOr(send.to, ""), amount: Math.max(0, Math.min(1, numberOr(send.amount, 0))) })) : [],
    envelopes: Array.isArray(value.envelopes) ? value.envelopes.filter(isRecord).map((env, index) => ({ id: stringOr(env.id, `env_${index + 1}`), track: stringOr(env.track, ""), param: stringOr(env.param, "gain"), points: Array.isArray(env.points) ? env.points.filter(isRecord).map((point) => ({ time: stringOr(point.time, "0"), value: numberOr(point.value, 0) })) : [] })) : [],
    plugins,
    master: { gain: numberOr(isRecord(value.master) ? value.master.gain : undefined, 0), limiter: isRecord(value.master) ? value.master.limiter !== false : true, ceilingDb: numberOr(isRecord(value.master) ? value.master.ceilingDb : undefined, -1), fx: [], plugins: [] },
  };
}

function readStoredProject(): DawProject {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? normalizeProject(JSON.parse(stored)) : null;
    return parsed ?? makeStarterProject();
  } catch {
    return makeStarterProject();
  }
}

function readStoredViewState(): DawViewState {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!stored) return defaultDawViewState;
    const parsed = JSON.parse(stored) as Partial<DawViewState>;
    return {
      arrangement: parsed.arrangement !== false,
      mixer: parsed.mixer !== false,
      inserts: parsed.inserts !== false,
    };
  } catch {
    return defaultDawViewState;
  }
}

function readStoredMixerHeight(): number {
  try {
    const storedValue = window.localStorage.getItem(MIXER_HEIGHT_STORAGE_KEY);
    if (!storedValue) return DEFAULT_MIXER_HEIGHT;
    const stored = Number(storedValue);
    return Number.isFinite(stored) ? Math.max(MIN_MIXER_HEIGHT, Math.min(MAX_MIXER_HEIGHT, stored)) : DEFAULT_MIXER_HEIGHT;
  } catch {
    return DEFAULT_MIXER_HEIGHT;
  }
}

function readStoredTrackLabelWidth(): number {
  try {
    const storedValue = window.localStorage.getItem(TRACK_LABEL_WIDTH_STORAGE_KEY);
    if (!storedValue) return DEFAULT_TRACK_LABEL_WIDTH;
    const stored = Number(storedValue);
    return Number.isFinite(stored)
      ? Math.max(MIN_TRACK_LABEL_WIDTH, Math.min(MAX_TRACK_LABEL_WIDTH, stored))
      : DEFAULT_TRACK_LABEL_WIDTH;
  } catch {
    return DEFAULT_TRACK_LABEL_WIDTH;
  }
}

function nextId(prefix: string, ids: string[]): string {
  const max = ids.reduce((highest, id) => {
    const match = id.match(/(\d+)$/);
    return Math.max(highest, match ? Number(match[1]) : 0);
  }, 0);
  return `${prefix}_${String(max + 1).padStart(2, "0")}`;
}

function projectDuration(project: DawProject): number {
  const secondsPerBar = (60 / project.tempo) * 4;
  let duration = 16;
  for (const track of project.tracks) {
    for (const clip of track.clips ?? []) duration = Math.max(duration, clip.start + (clip.length || 4));
    for (const clip of track.midiClips ?? []) duration = Math.max(duration, clip.start + (clip.length || 4));
    if (track.pattern) duration = Math.max(duration, (track.pattern.startBar - 1 + track.pattern.lengthBars) * secondsPerBar);
  }
  for (const section of project.arrangement) duration = Math.max(duration, section.start + section.length);
  return Math.ceil(duration);
}

function defaultTrackColor(type: TrackType): string {
  if (type === "drum") return "#e28c53";
  if (type === "midi") return "#66b89a";
  if (type === "bus") return "#e1a95f";
  return "#3f77a8";
}

function trackColor(track: Pick<DawTrack, "type" | "color">): string {
  return track.color || defaultTrackColor(track.type);
}

function waveformBars(value: string, count = 56): number[] {
  // Projects currently store media references rather than precomputed peaks.
  // Keep a stable, readable preview visible until the local engine stages the
  // source audio, instead of leaving the arrangement clip visually empty.
  let seed = 2166136261;
  for (const character of value) {
    seed ^= character.charCodeAt(0);
    seed = Math.imul(seed, 16777619);
  }
  return Array.from({ length: count }, (_, index) => {
    seed = Math.imul(seed ^ (index + 1), 16777619);
    const envelope = 0.35 + 0.65 * Math.abs(Math.sin(index * 0.61 + value.length));
    return Math.round((18 + ((seed >>> 0) % 82) * envelope) * 10) / 10;
  });
}

function DawClipWaveform({ file, waveform, color }: { file: string; waveform?: DawWaveform; color: string }) {
  const peaks = waveform?.peaks.length ? waveform.peaks : waveformBars(file).map((height) => height / 100);
  return (
    <span aria-label={waveform?.loading ? `Loading waveform for ${file}` : `Waveform preview for ${file}`} className={`daw-clip-waveform ${waveform?.loading ? "loading" : ""} ${waveform?.error ? "fallback" : ""}`} role="img">
      {peaks.map((peak, index) => <i key={`${file}-${index}`} style={{ background: color, height: `${Math.max(8, Math.min(100, peak * 100))}%` }} />)}
    </span>
  );
}

function DawTrackColorPicker({ track, onChange, className = "" }: { track: Pick<DawTrack, "id" | "name" | "type" | "color">; onChange: ChangeEventHandler<HTMLInputElement>; className?: string }) {
  const color = trackColor(track);
  return (
    <label className={`daw-track-color-picker ${className}`} title={`Change colour for ${track.name}`}>
      <span aria-hidden="true" style={{ background: color }} />
      <input aria-label={`Track colour for ${track.name}`} onChange={onChange} onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()} type="color" value={color} />
    </label>
  );
}

const TRACK_COLOR_PALETTE = [
  "#3f77a8", "#66b89a", "#e28c53", "#e1a95f", "#c96a6a", "#a86ac9",
  "#6aa8c9", "#8fbf5f", "#d86a8f", "#5fbfa8", "#c9a86a", "#6a6ac9",
  "#bf5f8f", "#5f8fbf", "#a8c96a", "#c96a8f", "#6ac9a8", "#8f6ac9",
];

function TrackColorMenu({ colorMenu, onClose, onPick }: { colorMenu: { trackId: string; x: number; y: number }; onClose: () => void; onPick: (color: string) => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="daw-track-color-menu" role="menu" style={{ left: colorMenu.x, top: colorMenu.y }}>
      <div className="daw-track-color-menu-title">Track colour</div>
      <div className="daw-track-color-menu-grid">
        {TRACK_COLOR_PALETTE.map((swatch) => (
          <button aria-label={`Set track colour ${swatch}`} className="daw-track-color-menu-swatch" key={swatch} onClick={() => onPick(swatch)} role="menuitem" style={{ background: swatch }} type="button" />
        ))}
      </div>
      <button className="daw-track-color-menu-close" onClick={onClose} type="button">Cancel</button>
    </div>
  );
}

function DawTrackGlyph({ track }: { track: Pick<DawTrack, "type"> }) {
  const Icon = track.type === "audio" ? Mic : track.type === "midi" ? Music2 : track.type === "bus" ? Route : AudioWaveform;
  return <Icon aria-hidden="true" className="h-7 w-7" />;
}

function inferredParameters(plugin: DawPlugin): PluginParameter[] {
  return Object.entries(plugin.params).map(([id, value]) => {
    if (typeof value === "boolean") return { id, name: id, kind: "boolean", value };
    if (typeof value === "number") return { id, name: id, kind: "number", value, minimum: 0, maximum: 1, step: 0.01 };
    return { id, name: id, kind: "choice", value, choices: [value] };
  });
}

function nativeParameters(info: NativePluginInfo): PluginParameter[] {
  return info.parameters.map((parameter) => {
    const step = parameter.discrete && parameter.numSteps > 1 ? 1 / Math.max(1, parameter.numSteps - 1) : 0.01;
    return {
      id: parameter.id,
      name: parameter.name || parameter.id,
      kind: parameter.boolean ? "boolean" : "number",
      value: parameter.value,
      minimum: 0,
      maximum: 1,
      step,
      unit: parameter.label || undefined,
      displayValue: parameter.text || undefined,
      defaultValue: parameter.defaultValue,
      automatable: parameter.automatable,
    };
  });
}

function parametersForPlugin(plugin: DawPlugin, info?: NativePluginInfo): PluginParameter[] {
  if (!info) return inferredParameters(plugin);
  return nativeParameters(info).map((parameter) => ({
    ...parameter,
    value: plugin.params[parameter.id] ?? parameter.value,
    displayValue: plugin.params[parameter.id] === undefined ? parameter.displayValue : undefined,
  }));
}

function prettyParameterName(value: string): string {
  return value.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").replace(/^./, (letter) => letter.toUpperCase());
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function renderBitDepth(format: RenderFormat): 16 | 24 | 32 {
  return format === "wav16" ? 16 : format === "wav32" ? 32 : 24;
}

function bounceFileName(projectName: string, format: RenderFormat, sampleRate: RenderSampleRate): string {
  const stem = projectName.replace(/[^a-z0-9 _.-]+/gi, "-").replace(/\s+/g, " ").trim() || "Untitled project";
  return `${stem} - Bounce ${sampleRate / 1000}kHz ${renderBitDepth(format)}-bit.wav`;
}

function formatDriveProjectDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

function formatLastSaved(timestamp: number): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

function bridgeHeaders(contentType = false): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (contentType) headers["Content-Type"] = "application/json";
  if (BRIDGE_TOKEN) headers.Authorization = `Bearer ${BRIDGE_TOKEN}`;
  return headers;
}

function encodeWaveFile(buffer: AudioBuffer): Blob {
  const channels = Math.min(2, buffer.numberOfChannels);
  const frameCount = buffer.length;
  const bytesPerSample = 2;
  const dataSize = frameCount * channels * bytesPerSample;
  const arrayBuffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(arrayBuffer);
  const writeText = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) view.setUint8(offset + index, value.charCodeAt(index));
  };
  writeText(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeText(8, "WAVE");
  writeText(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * channels * bytesPerSample, true);
  view.setUint16(32, channels * bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeText(36, "data");
  view.setUint32(40, dataSize, true);
  const channelData = Array.from({ length: channels }, (_, index) => buffer.getChannelData(index));
  let offset = 44;
  for (let frame = 0; frame < frameCount; frame += 1) {
    for (let channel = 0; channel < channels; channel += 1) {
      const sample = Math.max(-1, Math.min(1, channelData[channel][frame]));
      view.setInt16(offset, sample < 0 ? sample * 32768 : sample * 32767, true);
      offset += 2;
    }
  }
  return new Blob([arrayBuffer], { type: "audio/wav" });
}

async function convertAudioToWave(file: Blob): Promise<{ blob: Blob; duration: number }> {
  const audioContext = new AudioContext();
  try {
    const decoded = await audioContext.decodeAudioData(await file.arrayBuffer());
    return { blob: encodeWaveFile(decoded), duration: decoded.duration };
  } finally {
    await audioContext.close();
  }
}

async function requestPluginInfo(format: string, pluginPath: string): Promise<NativePluginInfo> {
  const response = await fetch(`${BRIDGE_URL}/plugin-info`, {
    method: "POST",
    headers: bridgeHeaders(true),
    body: JSON.stringify({ format, path: pluginPath }),
  });
  const result = await response.json() as { ok?: boolean; message?: string; plugin?: NativePluginInfo };
  if (!response.ok || !result.ok || !result.plugin) throw new Error(result.message || `HTTP ${response.status}`);
  return result.plugin;
}

function EngineBadge({ state }: { state: "checking" | "connected" | "offline" | "unconfigured" }) {
  const label = state === "connected" ? "Local engine" : state === "checking" ? "Checking…" : state === "unconfigured" ? "Local engine not running" : "Local engine offline";
  return <span className={`daw-engine-badge ${state}`}><span className="daw-engine-dot" />{label}</span>;
}

function DawFader({
  value,
  minimum,
  maximum,
  step,
  label,
  onChange,
  className = "",
}: {
  value: number;
  minimum: number;
  maximum: number;
  step: number;
  label: string;
  onChange: (value: number) => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const percentage = Math.max(0, Math.min(100, ((value - minimum) / (maximum - minimum)) * 100));

  const setFromClientY = useCallback((clientY: number) => {
    const bounds = ref.current?.getBoundingClientRect();
    if (!bounds || bounds.height <= 0) return;
    const ratio = Math.max(0, Math.min(1, 1 - ((clientY - bounds.top) / bounds.height)));
    const raw = minimum + ratio * (maximum - minimum);
    const stepped = Math.round(raw / step) * step;
    const decimals = Math.min(5, Math.max(0, String(step).split(".")[1]?.length ?? 0) + 1);
    onChange(Number(stepped.toFixed(decimals)));
  }, [maximum, minimum, onChange, step]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    let next: number | null = null;
    if (event.key === "ArrowUp" || event.key === "ArrowRight") next = value + step;
    if (event.key === "ArrowDown" || event.key === "ArrowLeft") next = value - step;
    if (event.key === "PageUp") next = value + step * 10;
    if (event.key === "PageDown") next = value - step * 10;
    if (event.key === "Home") next = minimum;
    if (event.key === "End") next = maximum;
    if (next === null) return;
    event.preventDefault();
    event.stopPropagation();
    onChange(Math.max(minimum, Math.min(maximum, next)));
  }

  return (
    <div
      aria-label={label}
      aria-orientation="vertical"
      aria-valuemax={maximum}
      aria-valuemin={minimum}
      aria-valuenow={value}
      className={`daw-fader-control ${dragging ? "dragging" : ""} ${className}`}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onChange(0);
      }}
      onKeyDown={handleKeyDown}
      onPointerCancel={(event) => {
        draggingRef.current = false;
        setDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        draggingRef.current = true;
        setDragging(true);
        setFromClientY(event.clientY);
      }}
      onPointerMove={(event) => {
        if (draggingRef.current) setFromClientY(event.clientY);
      }}
      onPointerUp={(event) => {
        draggingRef.current = false;
        setDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onWheel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        const next = value + (event.deltaY < 0 ? step : -step);
        onChange(Math.max(minimum, Math.min(maximum, next)));
      }}
      ref={ref}
      role="slider"
      tabIndex={0}
    >
      <span className="daw-fader-scale" aria-hidden="true" />
      <span className="daw-fader-track"><i style={{ height: `${percentage}%` }} /><b style={{ bottom: `calc(${percentage}% - 5px)` }} /></span>
    </div>
  );
}

function formatPan(value: number): string {
  if (Math.abs(value) < 0.005) return "C";
  return value < 0 ? `L${Math.round(Math.abs(value) * 100)}` : `R${Math.round(value * 100)}`;
}

function DawPanKnob({
  value,
  label,
  onChange,
  className = "",
}: {
  value: number;
  label: string;
  onChange: (value: number) => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const startYRef = useRef(0);
  const startValueRef = useRef(value);
  const [dragging, setDragging] = useState(false);
  const percentage = Math.max(0, Math.min(100, ((value + 1) / 2) * 100));
  const rotation = -135 + (percentage / 100) * 270;

  function setFromPointer(clientY: number) {
    const next = Math.max(-1, Math.min(1, startValueRef.current + ((startYRef.current - clientY) / 110) * 2));
    onChange(Number(next.toFixed(2)));
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    let next: number | null = null;
    if (event.key === "ArrowUp" || event.key === "ArrowRight") next = value + 0.01;
    if (event.key === "ArrowDown" || event.key === "ArrowLeft") next = value - 0.01;
    if (event.key === "PageUp") next = value + 0.1;
    if (event.key === "PageDown") next = value - 0.1;
    if (event.key === "Home") next = -1;
    if (event.key === "End") next = 1;
    if (next === null) return;
    event.preventDefault();
    event.stopPropagation();
    onChange(Number(Math.max(-1, Math.min(1, next)).toFixed(2)));
  }

  return (
    <div
      aria-label={label}
      aria-orientation="vertical"
      aria-valuemax={1}
      aria-valuemin={-1}
      aria-valuenow={value}
      aria-valuetext={formatPan(value)}
      className={`daw-pan-knob-control ${dragging ? "dragging" : ""} ${className}`}
      onDoubleClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onChange(0);
      }}
      onKeyDown={handleKeyDown}
      onPointerCancel={(event) => {
        draggingRef.current = false;
        setDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        draggingRef.current = true;
        startYRef.current = event.clientY;
        startValueRef.current = value;
        setDragging(true);
      }}
      onPointerMove={(event) => {
        if (draggingRef.current) setFromPointer(event.clientY);
      }}
      onPointerUp={(event) => {
        draggingRef.current = false;
        setDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onWheel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        const next = value + (event.deltaY < 0 ? 0.01 : -0.01);
        onChange(Number(Math.max(-1, Math.min(1, next)).toFixed(2)));
      }}
      ref={ref}
      role="slider"
      tabIndex={0}
      title={`${label} · drag vertically · double-click to center`}
    >
      <span className="daw-pan-knob-ticks" aria-hidden="true" />
      <span className="daw-pan-knob" style={{ transform: `rotate(${rotation}deg)` }}><i aria-hidden="true" /></span>
    </div>
  );
}

function PluginCard({
  plugin,
  info,
  onChange,
  onRemove,
  onToggle,
  canMoveUp,
  canMoveDown,
  onMove,
}: {
  plugin: DawPlugin;
  info?: NativePluginInfo;
  onChange: (...args: [string, ParamValue]) => void;
  onRemove: () => void;
  onToggle: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (direction: -1 | 1) => void;
}) {
  const parameters = parametersForPlugin(plugin, info);
  return (
    <article className={`daw-plugin-card ${plugin.enabled ? "" : "bypassed"}`}>
      <div className="daw-plugin-heading">
        <div>
          <span className="daw-plugin-format">{plugin.format}</span>
          <strong>{plugin.name}</strong>
          <small>{info ? `${info.manufacturer || "Native plugin"}${info.version ? ` · v${info.version}` : ""} · ${info.parameterCount} parameters` : plugin.path || "Waiting for Mac engine path"}</small>
        </div>
        <div className="daw-plugin-actions">
          <button aria-label="Move insert up" className="daw-icon-button" disabled={!canMoveUp} onClick={() => onMove(-1)} type="button"><ChevronUp className="h-4 w-4" /></button>
          <button aria-label="Move insert down" className="daw-icon-button" disabled={!canMoveDown} onClick={() => onMove(1)} type="button"><ChevronDown className="h-4 w-4" /></button>
          <button aria-label={plugin.enabled ? "Bypass plugin" : "Enable plugin"} className="daw-icon-button" onClick={onToggle} type="button">
            {plugin.enabled ? <Zap className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
          <button aria-label="Remove plugin" className="daw-icon-button danger" onClick={onRemove} type="button"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {parameters.length > 0 ? (
        <div className="daw-plugin-parameters">
          {parameters.map((parameter) => (
            <label className="daw-plugin-parameter" key={parameter.id}>
              <span><strong>{parameter.name === parameter.id ? prettyParameterName(parameter.name) : parameter.name}</strong><small>{parameter.unit ?? ""}</small></span>
              {parameter.kind === "boolean" ? (
                <input checked={Boolean(parameter.value)} onChange={(event) => onChange(parameter.id, event.target.checked)} type="checkbox" />
              ) : parameter.kind === "choice" ? (
                <select value={String(parameter.value)} onChange={(event) => onChange(parameter.id, event.target.value)}>
                  {(parameter.choices ?? [String(parameter.value)]).map((choice) => <option key={choice} value={choice}>{choice}</option>)}
                </select>
              ) : (
                <div className="daw-range-control">
                  <input max={parameter.maximum ?? 1} min={parameter.minimum ?? 0} onChange={(event) => onChange(parameter.id, Number(event.target.value))} step={parameter.step ?? 0.01} type="range" value={Number(parameter.value)} />
                  <output>{parameter.displayValue ?? (typeof parameter.value === "number" ? parameter.value.toFixed(parameter.step && parameter.step < 0.1 ? 2 : 1) : parameter.value)}</output>
                </div>
              )}
            </label>
          ))}
        </div>
      ) : (
        <p className="daw-plugin-empty">This entry is metadata-only until the Mac plugin registry is connected; native processing still happens on the Mac.</p>
      )}
    </article>
  );
}

function PluginInsertChain({
  track,
  plugins,
  availablePlugins,
  selection,
  onSelectionChange,
  onInsert,
  onSelectTrack,
}: {
  track: DawTrack;
  plugins: DawPlugin[];
  availablePlugins: InstalledPlugin[];
  selection: string;
  onSelectionChange: (value: string) => void;
  onInsert: (trackId: string, selection: string) => void;
  onSelectTrack: (trackId: string) => void;
}) {
  return (
    <div className="daw-insert-chain" onClick={(event) => event.stopPropagation()}>
      <span className="daw-insert-label">INSERTS</span>
      <div className="daw-insert-slots">
        {plugins.length > 0 ? plugins.map((plugin, index) => (
          <button className={`daw-insert-slot ${plugin.enabled ? "" : "bypassed"}`} key={plugin.id} onClick={() => onSelectTrack(track.id)} title={`${plugin.name}${plugin.enabled ? "" : " (bypassed)"} · click to edit`} type="button">
            <span>{index + 1}</span>{plugin.name}
          </button>
        )) : <span className="daw-no-inserts">No inserts</span>}
      </div>
      {availablePlugins.length > 0 ? (
        <div className="daw-insert-add">
          <select aria-label={`Choose insert for ${track.name}`} onChange={(event) => onSelectionChange(event.target.value)} value={selection}>
            <option value="">+ insert plugin</option>
            {availablePlugins.map((plugin) => <option key={`${plugin.format}|${plugin.path}`} value={`${plugin.format}|${plugin.path}`}>{plugin.descriptiveName || plugin.name} · {plugin.format}</option>)}
          </select>
          <button aria-label={`Add insert to ${track.name}`} disabled={!selection} onClick={() => onInsert(track.id, selection)} type="button"><Plus className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        <button className="daw-insert-manage" onClick={() => onSelectTrack(track.id)} title="Select this channel to scan or add a plugin" type="button"><Plus className="h-3.5 w-3.5" /> Insert</button>
      )}
    </div>
  );
}

export default function Daw({ desktop = false }: { desktop?: boolean }) {
  return (
    <AppearanceProvider>
      <IconProvider>
        {desktop ? <DawWorkspace desktop /> : <PortalDawWorkspace />}
      </IconProvider>
    </AppearanceProvider>
  );
}

function PortalDawWorkspace() {
  const { fetchAccessToken } = useConvexAuth();
  return <DawWorkspace fetchAccessToken={fetchAccessToken} />;
}

function DawWorkspace({ desktop = false, fetchAccessToken }: { desktop?: boolean; fetchAccessToken?: AccessTokenFetcher }) {
  const { fontFamily, theme } = useAppearance();
  const [project, setProject] = useState<DawProject>(readStoredProject);
  const [selectedTrackId, setSelectedTrackId] = useState("track_01");
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(() => {
    try { const t = window.localStorage.getItem(LAST_SAVED_KEY); return t ? Number(t) : null; } catch { return null; }
  });
  const [status, setStatus] = useState("Local project ready · JSON is the source of truth");
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [playheadDragging, setPlayheadDragging] = useState(false);
  const [engineState, setEngineState] = useState<"checking" | "connected" | "offline" | "unconfigured">("checking");
  const [newPluginName, setNewPluginName] = useState("");
  const [newPluginFormat, setNewPluginFormat] = useState<"AU" | "VST3">("VST3");
  const [newPluginSelection, setNewPluginSelection] = useState("");
  const [availablePlugins, setAvailablePlugins] = useState<InstalledPlugin[]>([]);
  const [pluginInfo, setPluginInfo] = useState<Record<string, NativePluginInfo>>({});
  const [isScanningPlugins, setIsScanningPlugins] = useState(false);
  const [pluginScanStatus, setPluginScanStatus] = useState("");
  const [insertSelectionByTrack, setInsertSelectionByTrack] = useState<Record<string, string>>({});
  const [viewState, setViewState] = useState<DawViewState>(readStoredViewState);
  const [mixerHeight, setMixerHeight] = useState(readStoredMixerHeight);
  const [isResizingMixer, setIsResizingMixer] = useState(false);
  const [trackLabelWidth, setTrackLabelWidth] = useState(readStoredTrackLabelWidth);
  const [isResizingTrackLabel, setIsResizingTrackLabel] = useState(false);
  const [timelineZoom, setTimelineZoom] = useState(52);
  const [fitTracks, setFitTracks] = useState(false);
  const [fitRowHeight, setFitRowHeight] = useState(58);
  const [fitHorizontalActive, setFitHorizontalActive] = useState(false);
  const [fitVerticalActive, setFitVerticalActive] = useState(false);
  const [preFitZoom, setPreFitZoom] = useState(52);
  const [preFitRowHeight, setPreFitRowHeight] = useState(58);
  const [autoFitRowHeight, setAutoFitRowHeight] = useState(DEFAULT_TRACK_ROW_HEIGHT);
  const [snapMode, setSnapMode] = useState<SnapMode>("Smart");
  const [dragMode, setDragMode] = useState<DragMode>("No Overlap");
  const [isCycleEnabled, setIsCycleEnabled] = useState(false);
  const [isMetronomeEnabled, setIsMetronomeEnabled] = useState(true);
  const [countInBars, setCountInBars] = useState(1);
  const [punchEnabled, setPunchEnabled] = useState(false);
  const [punchIn, setPunchIn] = useState(0);
  const [punchOut, setPunchOut] = useState(8);
  const [isRecording, setIsRecording] = useState(false);
  const [isPreparingPlayback, setIsPreparingPlayback] = useState(false);
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioInputId, setAudioInputId] = useState("");
  const [showRecordSettings, setShowRecordSettings] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [showBounceSettings, setShowBounceSettings] = useState(false);
  const [bounceFormat, setBounceFormat] = useState<RenderFormat>("wav24");
  const [bounceSampleRate, setBounceSampleRate] = useState<RenderSampleRate>(44100);
  const [isRendering, setIsRendering] = useState(false);
  const [isSavingBounce, setIsSavingBounce] = useState(false);
  const [bounceError, setBounceError] = useState("");
  const [bounceResult, setBounceResult] = useState<DawBounceResult | null>(null);
  const [driveProjects, setDriveProjects] = useState<DriveMiniDawProject[]>([]);
  const [isLoadingDriveProjects, setIsLoadingDriveProjects] = useState(false);
  const [driveProjectsError, setDriveProjectsError] = useState("");
  const [openingDriveProjectId, setOpeningDriveProjectId] = useState("");
  const [driveProjectId, setDriveProjectId] = useState("");
  const [driveProjectMedia, setDriveProjectMedia] = useState<DriveMiniDawMedia[]>([]);
  const [waveforms, setWaveforms] = useState<Record<string, DawWaveform>>({});
  const [showAppearanceSettings, setShowAppearanceSettings] = useState(false);
  const [trackMeters, setTrackMeters] = useState<number[]>([]);
  const [masterMeter, setMasterMeter] = useState(0);
  const [loopStart, setLoopStart] = useState(0);
  const [loopEnd, setLoopEnd] = useState(8);
  const [showMarkers, setShowMarkers] = useState(true);
  const [showAutomation, setShowAutomation] = useState(false);
  const [showEditTools, setShowEditTools] = useState(false);
  const [automationParameter, setAutomationParameter] = useState<"gain" | "pan">("gain");
  const [selectedClipId, setSelectedClipId] = useState("");
  const [mixerMode, setMixerMode] = useState<"tracks" | "all">("tracks");
  const [colorMenu, setColorMenu] = useState<{ trackId: string; x: number; y: number } | null>(null);
  const [sendDestination, setSendDestination] = useState("");
  const [undoDepth, setUndoDepth] = useState(0);
  const [redoDepth, setRedoDepth] = useState(0);
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const mediaInput = useRef<HTMLInputElement>(null);
  const undoStackRef = useRef<DawProject[]>([]);
  const redoStackRef = useRef<DawProject[]>([]);
  const historyGroupRef = useRef({ key: "", at: 0 });
  const clipDragRef = useRef<{ trackId: string; clipId: string; mode: "move" | "start" | "end"; startX: number; originalStart: number; originalLength: number; originalOffset: number } | null>(null);
  const automationDragRef = useRef<{ envelopeId: string; pointIndex: number } | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingTrackRef = useRef("");
  const recordingStartRef = useRef(0);
  const recordStartTimerRef = useRef<number | null>(null);
  const recordStopTimerRef = useRef<number | null>(null);
  const mixerResizeRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const trackLabelResizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const mixerHeightRef = useRef(mixerHeight);
  const trackLabelWidthRef = useRef(trackLabelWidth);
  const driveProjectRequestRef = useRef(0);
  const driveProjectsLoadedAtRef = useRef(0);
  const driveMediaLocalPathsRef = useRef(new Map<string, string>());
  const localMediaPathsRef = useRef(new Map<string, string>());
  const bounceProjectRevisionRef = useRef(0);
  const [localProjectFileName, setLocalProjectFileName] = useState("");
  const waveformRequestRef = useRef(0);
  const waveformCacheRef = useRef(new Map<string, DawWaveform>());

  const selectedTrack = project.tracks.find((track) => track.id === selectedTrackId) ?? project.tracks[0];
  const visibleTracks = project.tracks.filter((track) => !track.hidden);
  const duration = projectDuration(project);
  const secondsPerBar = (60 / project.tempo) * 4;
  const pixelsPerSecond = timelineZoom;
  const trackRowHeightDefault = DEFAULT_TRACK_ROW_HEIGHT;
  // Keep the JS timeline origin aligned with the resizable sticky track-label
  // column defined in Daw.css. The Logic-style channel header contains the
  // track identity, M/S + R/I controls, level slider, and pan knob.
  const leftWidth = trackLabelWidth;
  const timelineWidth = Math.max(leftWidth + 320, leftWidth + duration * pixelsPerSecond + 32);
  const autoFitShortTracks = !fitTracks && visibleTracks.length > 0 && visibleTracks.length <= AUTO_FIT_SHORT_TRACK_LIMIT;
  const trackRowHeight = fitTracks ? fitRowHeight : autoFitShortTracks ? autoFitRowHeight : trackRowHeightDefault;
  const selectedPlugins = selectedTrack ? project.plugins.filter((plugin) => plugin.track === selectedTrack.id) : [];
  const selectedClip = selectedTrack?.clips?.find((clip) => clip.id === selectedClipId);
  const busTracks = project.tracks.filter((track) => track.type === "bus");
  const selectedSends = selectedTrack ? project.sends.filter((send) => send.from === selectedTrack.id) : [];
  const mixerTracks = mixerMode === "all" ? project.tracks : visibleTracks;
  const currentBar = Math.floor(position / secondsPerBar) + 1;
  const currentBeat = Math.floor((position % secondsPerBar) / (60 / project.tempo)) + 1;
  const safeLoopStart = Math.max(0, Math.min(duration, loopStart));
  const safeLoopEnd = Math.max(safeLoopStart + 0.05, Math.min(duration, loopEnd));
  const safePunchIn = Math.max(0, Math.min(duration, punchIn));
  const safePunchOut = Math.max(safePunchIn + 0.05, Math.min(duration, punchOut));

  function setMixerHeightValue(value: number) {
    const next = Math.max(MIN_MIXER_HEIGHT, Math.min(MAX_MIXER_HEIGHT, Math.round(value)));
    mixerHeightRef.current = next;
    setMixerHeight(next);
    window.localStorage.setItem(MIXER_HEIGHT_STORAGE_KEY, String(next));
  }

  function beginMixerResize(event: ReactPointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    mixerResizeRef.current = { startY: event.clientY, startHeight: mixerHeightRef.current };
    setIsResizingMixer(true);
  }

  function resizeMixerByKeyboard(event: React.KeyboardEvent<HTMLButtonElement>) {
    let delta = 0;
    if (event.key === "ArrowUp") delta = 16;
    if (event.key === "ArrowDown") delta = -16;
    if (event.key === "Home") {
      event.preventDefault();
      setMixerHeightValue(MIN_MIXER_HEIGHT);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setMixerHeightValue(MAX_MIXER_HEIGHT);
      return;
    }
    if (!delta) return;
    event.preventDefault();
    setMixerHeightValue(mixerHeightRef.current + delta);
  }

  useEffect(() => {
    if (!isResizingMixer) return;
    const handlePointerMove = (event: globalThis.Event) => {
      const drag = mixerResizeRef.current;
      if (!drag) return;
      const clientY = (event as globalThis.Event & { clientY: number }).clientY;
      setMixerHeightValue(drag.startHeight - (clientY - drag.startY));
    };
    const finishResize = () => {
      mixerResizeRef.current = null;
      setIsResizingMixer(false);
      setStatus("Mixer height adjusted");
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", finishResize);
    window.addEventListener("pointercancel", finishResize);
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", finishResize);
      window.removeEventListener("pointercancel", finishResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizingMixer]);

  function setTrackLabelWidthValue(value: number) {
    const next = Math.max(MIN_TRACK_LABEL_WIDTH, Math.min(MAX_TRACK_LABEL_WIDTH, Math.round(value)));
    trackLabelWidthRef.current = next;
    setTrackLabelWidth(next);
    window.localStorage.setItem(TRACK_LABEL_WIDTH_STORAGE_KEY, String(next));
  }

  function beginTrackLabelResize(event: ReactPointerEvent<HTMLElement>) {
    event.preventDefault();
    event.stopPropagation();
    trackLabelResizeRef.current = { startX: event.clientX, startWidth: trackLabelWidthRef.current };
    setIsResizingTrackLabel(true);
  }

  function resizeTrackLabelByKeyboard(event: React.KeyboardEvent<HTMLButtonElement>) {
    let delta = 0;
    if (event.key === "ArrowLeft") delta = -16;
    if (event.key === "ArrowRight") delta = 16;
    if (event.key === "Home") {
      event.preventDefault();
      setTrackLabelWidthValue(MIN_TRACK_LABEL_WIDTH);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setTrackLabelWidthValue(MAX_TRACK_LABEL_WIDTH);
      return;
    }
    if (!delta) return;
    event.preventDefault();
    setTrackLabelWidthValue(trackLabelWidthRef.current + delta);
    setStatus(`Track controls width set to ${trackLabelWidthRef.current}px`);
  }

  useEffect(() => {
    if (!isResizingTrackLabel) return;
    const handlePointerMove = (event: globalThis.Event) => {
      const drag = trackLabelResizeRef.current;
      if (!drag) return;
      const clientX = (event as globalThis.Event & { clientX: number }).clientX;
      setTrackLabelWidthValue(drag.startWidth + (clientX - drag.startX));
    };
    const finishResize = () => {
      trackLabelResizeRef.current = null;
      setIsResizingTrackLabel(false);
      setStatus(`Track controls width set to ${trackLabelWidthRef.current}px`);
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", finishResize);
    window.addEventListener("pointercancel", finishResize);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", finishResize);
      window.removeEventListener("pointercancel", finishResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizingTrackLabel]);

  function toggleViewSection(key: DawViewKey) {
    setViewState((current) => {
      if (current[key] && Object.values(current).filter(Boolean).length === 1) {
        setStatus("Keep one DAW section visible");
        return current;
      }
      const next = { ...current, [key]: !current[key] };
      window.localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }

  function setManualTimelineZoom(value: number) {
    setTimelineZoom(Math.max(4, Math.min(140, value)));
    setFitTracks(false);
    setFitRowHeight(trackRowHeightDefault);
  }

  function fitTimelineHorizontal() {
    if (fitHorizontalActive) {
      // Revert to the pre-fit zoom.
      setTimelineZoom(preFitZoom);
      setFitHorizontalActive(false);
      setStatus("Timeline fit reverted");
      return;
    }
    const viewport = timelineScrollRef.current;
    const viewportWidth = viewport?.clientWidth ?? 960;
    const usableWidth = Math.max(180, viewportWidth - leftWidth - 32);
    const fittedZoom = Math.min(140, Math.max(4, usableWidth / Math.max(1, duration)));
    setPreFitZoom(timelineZoom);
    setTimelineZoom(fittedZoom);
    setFitHorizontalActive(true);
    // Keep the vertical fit state intact; only adjust the horizontal zoom.
    requestAnimationFrame(() => timelineScrollRef.current?.scrollTo({ left: 0, behavior: "smooth" }));
    setStatus("Timeline fitted horizontally");
  }

  function fitTimelineVertical() {
    if (fitVerticalActive) {
      // Revert to the pre-fit row height and clear the vertical fit.
      setFitRowHeight(preFitRowHeight);
      setFitTracks(false);
      setFitVerticalActive(false);
      setStatus("Tracks fit reverted");
      return;
    }
    const viewport = timelineScrollRef.current;
    const viewportHeight = timelineViewportHeight(viewport);
    const fittedRowHeight = fittedTimelineRowHeight(viewportHeight, visibleTracks.length, project.arrangement.length > 0, showMarkers);

    setPreFitRowHeight(fitRowHeight);
    setFitRowHeight(fittedRowHeight);
    setFitTracks(true);
    setFitVerticalActive(true);

    // Recompute once layout has settled so the scroll container reflects the
    // new canvas size before we scroll to origin.
    requestAnimationFrame(() => {
      timelineScrollRef.current?.scrollTo({ left: 0, top: 0 });
    });
    setStatus("Tracks fitted vertically");
  }

  function fitTimelineToWindow() {
    if (fitHorizontalActive && fitVerticalActive) {
      // Revert both.
      setTimelineZoom(preFitZoom);
      setFitRowHeight(preFitRowHeight);
      setFitTracks(false);
      setFitHorizontalActive(false);
      setFitVerticalActive(false);
      setStatus("Timeline fit reverted");
      return;
    }
    const viewport = timelineScrollRef.current;
    const viewportWidth = viewport?.clientWidth ?? 960;
    const usableWidth = Math.max(180, viewportWidth - leftWidth - 32);
    const fittedZoom = Math.min(140, Math.max(4, usableWidth / Math.max(1, duration)));
    const viewportHeight = timelineViewportHeight(viewport);
    const fittedRowHeight = fittedTimelineRowHeight(viewportHeight, visibleTracks.length, project.arrangement.length > 0, showMarkers);
    setPreFitZoom(timelineZoom);
    setPreFitRowHeight(fitRowHeight);
    setTimelineZoom(fittedZoom);
    setFitRowHeight(fittedRowHeight);
    setFitTracks(true);
    setFitHorizontalActive(true);
    setFitVerticalActive(true);
    requestAnimationFrame(() => {
      timelineScrollRef.current?.scrollTo({ left: 0, top: 0 });
    });
    setStatus("Timeline fitted horizontally and vertically");
  }

  // Keep Fit H+V honest when the mixer/inspector is toggled or the browser
  // viewport changes after the user has already fitted the session.
  useEffect(() => {
    if (!fitTracks) return;
    const viewport = timelineScrollRef.current;
    if (!viewport) return;
    const recomputeFit = () => {
      const usableWidth = Math.max(180, viewport.clientWidth - leftWidth - 32);
      const fittedZoom = Math.min(140, Math.max(4, usableWidth / Math.max(1, duration)));
      const fittedRowHeight = fittedTimelineRowHeight(timelineViewportHeight(viewport), visibleTracks.length, project.arrangement.length > 0, showMarkers);
      setTimelineZoom(fittedZoom);
      setFitRowHeight(fittedRowHeight);
    };
    recomputeFit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(recomputeFit);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [duration, fitTracks, leftWidth, project.arrangement.length, showMarkers, trackRowHeightDefault, visibleTracks.length]);

  // Short sessions should use the available arrangement height by default.
  // Larger sessions keep the readable fixed row height and can use Fit V on demand.
  useEffect(() => {
    if (!autoFitShortTracks) return;
    const viewport = timelineScrollRef.current;
    if (!viewport) return;
    const recomputeAutoFit = () => {
      setAutoFitRowHeight(fittedTimelineRowHeight(timelineViewportHeight(viewport), visibleTracks.length, project.arrangement.length > 0, showMarkers));
    };
    recomputeAutoFit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(recomputeAutoFit);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [autoFitShortTracks, project.arrangement.length, showMarkers, visibleTracks.length]);

  function snapTimelinePosition(value: number): number {
    if (snapMode === "Off") return value;
    const interval = snapMode === "Beat" ? 60 / project.tempo : secondsPerBar;
    return Math.round(value / interval) * interval;
  }

  function seekTimeline(event: MouseEvent<HTMLDivElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const rawPosition = (event.clientX - bounds.left) / pixelsPerSecond;
    const nextPosition = Math.max(0, Math.min(duration, snapTimelinePosition(rawPosition)));
    setPosition(nextPosition);
    setStatus(`Playhead moved to ${formatTime(nextPosition)}`);
  }

  useEffect(() => {
    let cancelled = false;
    const checkBridge = async () => {
      try {
        const response = await fetch(`${BRIDGE_URL}/health`, { headers: bridgeHeaders(), cache: "no-store" });
        const payload = await response.json() as { ok?: boolean };
        if (!response.ok || !payload.ok) throw new Error(`HTTP ${response.status}`);
        if (!cancelled) setEngineState("connected");
      } catch {
        if (!cancelled) setEngineState("offline");
      }
    };
    void checkBridge();
    const timer = window.setInterval(() => { void checkBridge(); }, 2500);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, select, textarea, [contenteditable=\"true\"]")) return;
      const command = event.metaKey || event.ctrlKey;
      if (command && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      } else if (command && event.key.toLowerCase() === "d" && selectedClip) {
        event.preventDefault();
        duplicateSelectedClip();
      } else if (event.code === "Space") {
        event.preventDefault();
        if (isPlaying) void stopNativePlayback();
        else void startNativePlayback();
      } else if (event.key.toLowerCase() === "x") {
        toggleViewSection("mixer");
      } else if (event.key.toLowerCase() === "i") {
        toggleViewSection("inserts");
      } else if (event.key.toLowerCase() === "a") {
        setShowAutomation((current) => !current);
      } else if (event.key.toLowerCase() === "g") {
        setShowMarkers((current) => !current);
      } else if (event.key.toLowerCase() === "r") {
        void togglePortalRecording();
      } else if (command && event.key.toLowerCase() === "s") {
        event.preventDefault();
        saveProject();
      } else if ((event.key === "Backspace" || event.key === "Delete") && selectedClip) {
        event.preventDefault();
        deleteSelectedClip();
      } else if (event.key === "Escape" && isPlaying) {
        void stopNativePlayback();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPlaying, isRecording, project, selectedClip, selectedTrack, viewState, isMetronomeEnabled, isCycleEnabled, loopStart, loopEnd, position, countInBars, punchEnabled, punchIn, punchOut, audioInputId]);

  useEffect(() => () => {
    if (recordStartTimerRef.current !== null) window.clearTimeout(recordStartTimerRef.current);
    if (recordStopTimerRef.current !== null) window.clearTimeout(recordStopTimerRef.current);
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  useEffect(() => {
    if (engineState !== "connected" || !BRIDGE_URL) return;
    const pending = project.plugins.filter((plugin) => plugin.path && !pluginInfo[plugin.id]);
    if (pending.length === 0) return;
    let cancelled = false;
    void Promise.all(pending.map(async (plugin) => {
      if (!plugin.path) return;
      try {
        const info = await requestPluginInfo(plugin.format, plugin.path);
        if (!cancelled) setPluginInfo((current) => current[plugin.id] ? current : { ...current, [plugin.id]: info });
      } catch {
        // Missing or incompatible plugins remain safe metadata-only entries.
      }
    }));
    return () => { cancelled = true; };
  }, [engineState, pluginInfo, project.plugins]);

  useEffect(() => {
    if (engineState !== "connected" || !BRIDGE_URL) return;
    let cancelled = false;
    let lastState: NativeTransport["state"] | null = null;
    const pollTransport = async () => {
      try {
        const response = await fetch(`${BRIDGE_URL}/transport/status`, { headers: bridgeHeaders() });
        const result = await response.json() as { ok?: boolean; message?: string; transport?: NativeTransport };
        if (!response.ok || !result.ok || !result.transport) throw new Error(result.message || `HTTP ${response.status}`);
        if (cancelled) return;
        const transport = result.transport;
        const playing = transport.state === "playing";
        setIsPlaying(playing);
        // Only follow the engine position while playing. When stopped, keep the
        // locally dragged/clicked position so the playhead doesn't snap back.
        if (playing && !playheadDragging) setPosition(Math.max(0, Math.min(duration, Number(transport.position) || 0)));
        setMasterMeter(Math.max(0, Math.min(1, Number(transport.meters?.master) || 0)));
        setTrackMeters(Array.isArray(transport.meters?.tracks) ? transport.meters.tracks : []);
        if (lastState !== null && lastState !== transport.state) {
          if (transport.state === "error") setStatus(`JUCE playback failed · ${transport.error || "native engine stopped"}`);
          else if (transport.state === "stopped") setStatus(transport.position >= duration - 0.05 ? "Playback finished" : "Stopped");
        }
        lastState = transport.state;
      } catch (error) {
        if (!cancelled) {
          setIsPlaying(false);
          setMasterMeter(0);
          setTrackMeters([]);
          setEngineState("offline");
          setStatus(`Mac JUCE transport unavailable · ${error instanceof Error ? error.message : "bridge unavailable"}`);
        }
      }
    };
    void pollTransport();
    const timer = window.setInterval(() => { void pollTransport(); }, 250);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [duration, engineState, playheadDragging]);

  useEffect(() => {
    if (!isPlaying) return;
    const viewport = timelineScrollRef.current;
    if (!viewport) return;
    const playheadX = leftWidth + position * pixelsPerSecond;
    const rightEdge = viewport.scrollLeft + viewport.clientWidth - 48;
    if (playheadX > rightEdge) viewport.scrollLeft = Math.max(0, playheadX - viewport.clientWidth * 0.35);
  }, [isPlaying, leftWidth, pixelsPerSecond, position]);

  // Close toolbar disclosures on Escape.
  useEffect(() => {
    if (!showProjectMenu && !showRecordSettings && !showAppearanceSettings && !showBounceSettings) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setShowProjectMenu(false);
        setShowRecordSettings(false);
        setShowAppearanceSettings(false);
        setShowBounceSettings(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showAppearanceSettings, showBounceSettings, showProjectMenu, showRecordSettings]);

  useEffect(() => {
    bounceProjectRevisionRef.current += 1;
    setBounceResult(null);
    setBounceError("");
  }, [project]);

  function rememberSnapshot(snapshot: DawProject, historyKey: string) {
    const now = Date.now();
    const grouped = historyGroupRef.current.key === historyKey && now - historyGroupRef.current.at < 650;
    if (!grouped) {
      undoStackRef.current.push(cloneProject(snapshot));
      if (undoStackRef.current.length > 60) undoStackRef.current.shift();
      redoStackRef.current = [];
      setUndoDepth(undoStackRef.current.length);
      setRedoDepth(0);
    }
    historyGroupRef.current = { key: historyKey, at: now };
  }

  function mutate(mutator: (...args: [DawProject]) => void, message: string, historyKey = message) {
    setProject((current) => {
      rememberSnapshot(current, historyKey);
      const next = cloneProject(current);
      mutator(next);
      return next;
    });
    setIsDirty(true);
    setStatus(`Edited · ${message}`);
  }

  function replaceProject(next: DawProject, message: string, dirty = true) {
    setProject((current) => {
      rememberSnapshot(current, `replace:${message}:${Date.now()}`);
      return cloneProject(next);
    });
    setIsDirty(dirty);
    setStatus(message);
  }

  function undo() {
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    setProject((current) => {
      redoStackRef.current.push(cloneProject(current));
      return cloneProject(previous);
    });
    historyGroupRef.current = { key: "", at: 0 };
    setUndoDepth(undoStackRef.current.length);
    setRedoDepth(redoStackRef.current.length);
    setIsDirty(true);
    setStatus("Undid last edit");
  }

  function redo() {
    const next = redoStackRef.current.pop();
    if (!next) return;
    setProject((current) => {
      undoStackRef.current.push(cloneProject(current));
      return cloneProject(next);
    });
    historyGroupRef.current = { key: "", at: 0 };
    setUndoDepth(undoStackRef.current.length);
    setRedoDepth(redoStackRef.current.length);
    setIsDirty(true);
    setStatus("Redid last edit");
  }

  function saveProject() {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(project));
    const now = Date.now();
    try { window.localStorage.setItem(LAST_SAVED_KEY, String(now)); } catch { /* storage may be disabled */ }
    setLastSavedAt(now);
    setIsDirty(false);
    setStatus("Saved locally · ready to send to the Mac engine");
  }

  function downloadProject() {
    const blob = new window.Blob([JSON.stringify(project, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${project.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "mini-daw-project"}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setStatus("Exported project JSON");
  }

  function resetProject() {
    if (isDirty && !window.confirm("Discard unsaved DAW changes and start a new project?")) return;
    const next = makeStarterProject();
    resetWaveformState();
    setDriveProjectId("");
    setDriveProjectMedia([]);
    localMediaPathsRef.current.clear();
    setLocalProjectFileName("");
    replaceProject(next, "New project created · press Save to keep it locally");
    setSelectedTrackId(next.tracks[0].id);
    setPosition(0);
    setSelectedClipId("");
  }

  function importProject(file: File) {
    void file.text().then((text) => {
      try {
        const next = normalizeProject(JSON.parse(text));
        if (!next) throw new Error("The file is not a mini-daw project");
        resetWaveformState();
        setDriveProjectId("");
        setDriveProjectMedia([]);
        localMediaPathsRef.current.clear();
        setLocalProjectFileName("");
        replaceProject(next, `Imported ${file.name} · review and Save`);
        setSelectedTrackId(next.tracks[0]?.id ?? "");
        setPosition(0);
        setSelectedClipId("");
      } catch (error) {
        setStatus(`Import failed · ${error instanceof Error ? error.message : "invalid JSON"}`);
      }
    });
  }

  function applyDesktopProject(payload: DesktopProjectPayload | null) {
    if (!payload) return;
    if (!payload.ok) {
      setStatus(`Local project failed to open · ${payload.error || "invalid project"}`);
      return;
    }
    if (isDirty && !window.confirm("Discard unsaved DAW changes and open this local project?")) return;
    const next = normalizeProject(payload.project);
    if (!next || !payload.fileName) {
      setStatus("Local project failed to open · invalid Mini-DAW JSON");
      return;
    }
    resetWaveformState();
    setDriveProjectId("");
    setDriveProjectMedia([]);
    localMediaPathsRef.current = new Map((payload.media ?? []).map((item) => [item.reference, item.path]));
    setLocalProjectFileName(payload.fileName);
    replaceProject(next, `Opened ${payload.fileName} · local project loaded`, false);
    setSelectedTrackId(next.tracks[0]?.id ?? "");
    setPosition(0);
    setSelectedClipId("");
    setShowProjectMenu(false);
    const resolvedCount = payload.media?.length ?? 0;
    const missingCount = payload.missingMedia?.length ?? 0;
    setStatus(`Opened ${payload.fileName} · ${resolvedCount} local audio reference${resolvedCount === 1 ? "" : "s"} resolved${missingCount ? ` · ${missingCount} missing` : ""}`);
  }

  async function openDesktopProject() {
    const api = miniDawDesktopApi();
    if (!api) {
      setStatus("Local desktop project picker is unavailable");
      return;
    }
    applyDesktopProject(await api.openProject());
  }

  useEffect(() => {
    if (!desktop) return;
    const api = miniDawDesktopApi();
    return api?.onOpenProject((payload) => applyDesktopProject(payload));
  }, [desktop, isDirty]);

  async function fetchDriveApi(input: string) {
    if (!fetchAccessToken) throw new Error("Drive projects are available in the hosted Artist Portal");
    const request = async (token: string) => {
      return fetch(input, {
        cache: "no-store",
        headers: { Authorization: `Bearer ${token}` },
      });
    };

    let token = await fetchAccessToken({ forceRefreshToken: false });
    if (!token) throw new Error("Your portal session has expired");
    let response = await request(token);
    if (response.status === 401) {
      token = await fetchAccessToken({ forceRefreshToken: true });
      if (!token) throw new Error("Your portal session has expired");
      response = await request(token);
    }
    for (let attempt = 0; attempt < 2 && [408, 429, 500, 502, 503, 504].includes(response.status); attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
      response = await request(token);
    }
    return response;
  }

  async function loadDriveProjects(forceRefresh = false) {
    if (!forceRefresh && driveProjectsLoadedAtRef.current > 0 && Date.now() - driveProjectsLoadedAtRef.current < DRIVE_PROJECT_LIST_CACHE_TTL_MS) return;
    const requestId = driveProjectRequestRef.current + 1;
    driveProjectRequestRef.current = requestId;
    setIsLoadingDriveProjects(true);
    setDriveProjectsError("");
    try {
      const response = await fetchDriveApi(forceRefresh ? "/api/mini-daw-projects?refresh=1" : "/api/mini-daw-projects");
      const payload = await response.json().catch(() => null) as {
        projects?: DriveMiniDawProject[];
        error?: string;
      } | null;
      if (!response.ok) throw new Error(payload?.error || `Drive lookup failed (${response.status})`);
      if (!Array.isArray(payload?.projects)) throw new Error("Drive returned an invalid project list");
      if (requestId === driveProjectRequestRef.current) {
        setDriveProjects(payload.projects);
        driveProjectsLoadedAtRef.current = Date.now();
      }
    } catch (error) {
      if (requestId === driveProjectRequestRef.current) {
        setDriveProjectsError(error instanceof Error ? error.message : "Drive lookup failed");
      }
    } finally {
      if (requestId === driveProjectRequestRef.current) setIsLoadingDriveProjects(false);
    }
  }

  function resetWaveformState() {
    waveformRequestRef.current += 1;
    setWaveforms({});
  }

  async function loadDriveWaveforms(projectId: string, sourceProject: DawProject, media: DriveMiniDawMedia[], requestId: number) {
    const referencesByMedia = new Map<string, { descriptor: DriveMiniDawMedia; references: string[] }>();
    for (const reference of projectMediaReferences(sourceProject)) {
      const descriptor = findDriveMediaReference(reference, media);
      if (!descriptor) continue;
      const current = referencesByMedia.get(descriptor.id);
      if (current) current.references.push(reference);
      else referencesByMedia.set(descriptor.id, { descriptor, references: [reference] });
    }

    if (referencesByMedia.size === 0 || requestId !== waveformRequestRef.current) return;
    setWaveforms((current) => {
      const next = { ...current };
      for (const item of referencesByMedia.values()) {
        for (const reference of item.references) next[normalizeMediaPath(reference).toLocaleLowerCase()] = { peaks: [], rms: [], loading: true };
      }
      return next;
    });

    const queue = [...referencesByMedia.values()];
    const loadOne = async (item: { descriptor: DriveMiniDawMedia; references: string[] }) => {
      if (requestId !== waveformRequestRef.current) return;
      const cacheKey = `${projectId}:${item.descriptor.id}`;
      let waveform = waveformCacheRef.current.get(cacheKey);
      if (!waveform) {
        try {
          const response = await fetchDriveApi(`/api/mini-daw-projects?id=${encodeURIComponent(projectId)}&mediaId=${encodeURIComponent(item.descriptor.id)}`);
          if (!response.ok) throw new Error(`Waveform audio failed (${response.status})`);
          const source = await response.blob();
          const objectUrl = URL.createObjectURL(source);
          try {
            const analysis = await analyzeAudio(objectUrl, 120);
            waveform = { peaks: Array.from(analysis.peaks.peaks), rms: Array.from(analysis.peaks.rms) };
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        } catch {
          waveform = { peaks: [], rms: [], error: true };
        }
        waveformCacheRef.current.set(cacheKey, waveform);
      }
      if (requestId !== waveformRequestRef.current) return;
      setWaveforms((current) => {
        const next = { ...current };
        for (const reference of item.references) next[normalizeMediaPath(reference).toLocaleLowerCase()] = waveform as DawWaveform;
        return next;
      });
    };

    const workers = Array.from({ length: Math.min(2, queue.length) }, async () => {
      while (queue.length > 0 && requestId === waveformRequestRef.current) {
        const item = queue.shift();
        if (item) await loadOne(item);
      }
    });
    await Promise.all(workers);
  }

  async function openDriveProject(driveProject: DriveMiniDawProject) {
    if (isDirty && !window.confirm("Discard unsaved DAW changes and open this Drive project?")) return;
    setOpeningDriveProjectId(driveProject.id);
    setStatus(`Opening ${driveProject.name} from Drive…`);
    try {
      const response = await fetchDriveApi(`/api/mini-daw-projects?id=${encodeURIComponent(driveProject.id)}`);
      const payload = await response.json().catch(() => null) as {
        project?: unknown;
        media?: DriveMiniDawMedia[];
        error?: string;
      } | null;
      if (!response.ok) throw new Error(payload?.error || `Drive project could not be opened (${response.status})`);
      const next = normalizeProject(payload?.project);
      if (!next) throw new Error("The Drive JSON is not a Mini-DAW project");
      const media = Array.isArray(payload?.media) ? payload.media : [];
      const waveformRequestId = waveformRequestRef.current + 1;
      waveformRequestRef.current = waveformRequestId;
      setWaveforms({});
      setDriveProjectId(driveProject.id);
      setDriveProjectMedia(media);
      localMediaPathsRef.current.clear();
      setLocalProjectFileName("");
      replaceProject(next, `Opened ${driveProject.name} · Drive project loaded`, false);
      const folders = [...new Set(media.map((item) => item.relativePath.includes("/") ? item.relativePath.split("/")[0] : "Project root"))];
      setStatus(media.length > 0
        ? `Opened ${driveProject.name} · ${media.length} Drive audio files detected${folders.length > 0 ? ` · ${folders.join(", ")}` : ""}`
        : `Opened ${driveProject.name} · no Drive audio files found in this project folder`);
      setSelectedTrackId(next.tracks[0]?.id ?? "");
      setPosition(0);
      setSelectedClipId("");
      setShowProjectMenu(false);
      void loadDriveWaveforms(driveProject.id, next, media, waveformRequestId);
    } catch (error) {
      setStatus(`Drive project failed to open · ${error instanceof Error ? error.message : "invalid project"}`);
    } finally {
      setOpeningDriveProjectId("");
    }
  }

  async function uploadWave(blob: Blob, fileName: string): Promise<{ file: string; name: string }> {
    const headers = bridgeHeaders();
    headers["Content-Type"] = "audio/wav";
    headers["X-Mini-Daw-Filename"] = fileName;
    const response = await fetch(`${BRIDGE_URL}/media/import`, { method: "POST", headers, body: blob });
    const result = await response.json() as { ok?: boolean; message?: string; file?: string; name?: string };
    if (!response.ok || !result.ok || !result.file) throw new Error(result.message || `HTTP ${response.status}`);
    return { file: result.file, name: result.name || fileName };
  }

  async function prepareProjectForEngine(sourceProject: DawProject): Promise<EngineDawProject> {
    const references = projectMediaReferences(sourceProject).filter((value) => {
      const trimmed = value.trim();
      return !/^https?:\/\//i.test(trimmed) && !trimmed.startsWith("/") && !/^[A-Za-z]:[\\/]/.test(trimmed);
    });
    if (localProjectFileName && references.length > 0) {
      const missing = references.filter((reference) => !localMediaPathsRef.current.has(reference));
      if (missing.length > 0) {
        throw new Error(`Local media: could not find ${missing.join(", ")} beside ${localProjectFileName}`);
      }
      const next = cloneProject(sourceProject);
      for (const track of next.tracks) {
        if (track.kit) track.kit = localMediaPathsRef.current.get(track.kit) ?? track.kit;
        if (track.clips) {
          for (const clip of track.clips) clip.file = localMediaPathsRef.current.get(clip.file) ?? clip.file;
        }
      }
      return toEngineProject(next);
    }
    if (!driveProjectId || references.length === 0) return toEngineProject(sourceProject);
    if (driveProjectMedia.length === 0) {
      throw new Error("Drive media: this project has no audio files available in its project folder");
    }

    const replacements = new Map<string, string>();
    const missing: string[] = [];
    for (const reference of references) {
      const descriptor = findDriveMediaReference(reference, driveProjectMedia);
      if (!descriptor) {
        missing.push(reference);
        continue;
      }

      let localPath = driveMediaLocalPathsRef.current.get(descriptor.id);
      if (!localPath) {
        setStatus(`Staging ${descriptor.relativePath} from Drive…`);
        const response = await fetchDriveApi(`/api/mini-daw-projects?id=${encodeURIComponent(driveProjectId)}&mediaId=${encodeURIComponent(descriptor.id)}`);
        const payload = await response.json().catch(() => null) as { error?: string } | null;
        if (!response.ok) throw new Error(`Drive media: ${payload?.error || `audio download failed (${response.status})`}`);
        const source = await response.blob();
        const uploaded = await uploadWave(source, descriptor.name);
        localPath = uploaded.file;
        driveMediaLocalPathsRef.current.set(descriptor.id, localPath);
      }
      replacements.set(reference, localPath);
    }

    if (missing.length > 0) {
      throw new Error(`Drive media: could not find ${missing.join(", ")} inside the project folder`);
    }

    const next = cloneProject(sourceProject);
    for (const track of next.tracks) {
      if (track.kit) track.kit = replacements.get(track.kit) ?? track.kit;
      if (track.clips) {
        for (const clip of track.clips) clip.file = replacements.get(clip.file) ?? clip.file;
      }
    }
    return toEngineProject(next);
  }

  async function renderBounce() {
    if (isRendering) return;
    if (engineState !== "connected") {
      setBounceError("Start the local Mini-DAW bridge before rendering.");
      setStatus("Bounce unavailable · local engine is offline");
      return;
    }

    setBounceError("");
    setBounceResult(null);
    setIsRendering(true);
    const renderProject = project;
    const renderRevision = bounceProjectRevisionRef.current;
    try {
      if (isPlaying) await stopNativePlayback();
      setStatus("Preparing bounce · resolving audio media…");
      const prepared = await prepareProjectForEngine(renderProject);
      setStatus(`Rendering ${renderProject.name} through the Mac JUCE engine…`);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 11 * 60 * 1000);
      let response: Response;
      try {
        response = await fetch(`${BRIDGE_URL}/render`, {
          method: "POST",
          headers: bridgeHeaders(true),
          body: JSON.stringify({ project: prepared, format: bounceFormat, sampleRate: bounceSampleRate }),
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeout);
      }

      const payload = await response.json().catch(() => null) as DawRenderResponse | null;
      if (!response.ok || !payload?.ok || !payload.id || !payload.outputUrl) {
        throw new Error(payload?.message || `Bounce failed (HTTP ${response.status})`);
      }

      if (renderRevision !== bounceProjectRevisionRef.current) {
        setBounceError("Project changed while rendering. Run Bounce again.");
        setStatus("Bounce discarded · project changed while rendering");
        return;
      }

      const data = payload.result?.data ?? {};
      const requestedSampleRate = Number(data.sampleRate);
      const requestedBitDepth = Number(data.bitDepth);
      const requestedChannels = Number(data.channels);
      const requestedDuration = Number(data.duration ?? data.durationSeconds);
      const sampleRate = [44100, 48000, 88200, 96000].includes(requestedSampleRate)
        ? requestedSampleRate as RenderSampleRate
        : bounceSampleRate;
      const bitDepth = [16, 24, 32].includes(requestedBitDepth)
        ? requestedBitDepth as 16 | 24 | 32
        : renderBitDepth(bounceFormat);
      const result: DawBounceResult = {
        id: payload.id,
        outputUrl: payload.outputUrl,
        fileName: payload.fileName || bounceFileName(renderProject.name, bounceFormat, bounceSampleRate),
        format: bounceFormat,
        sampleRate,
        bitDepth,
        channels: Number.isFinite(requestedChannels) && requestedChannels > 0 ? requestedChannels : 2,
        durationSeconds: Number.isFinite(requestedDuration) && requestedDuration > 0 ? requestedDuration : duration,
        manifestHash: payload.result?.manifest?.manifestHash,
      };
      setBounceResult(result);
      setStatus(`Bounce ready · ${result.fileName}`);
    } catch (error) {
      const message = error instanceof DOMException && error.name === "AbortError"
        ? "Render timed out"
        : error instanceof Error ? error.message : "Bounce failed";
      setBounceError(message);
      setStatus(`Bounce failed · ${message}`);
    } finally {
      setIsRendering(false);
    }
  }

  async function saveBounce() {
    if (!bounceResult || isSavingBounce) return;
    setIsSavingBounce(true);
    try {
      if (desktop) {
        const api = miniDawDesktopApi();
        if (api?.saveRender) {
          const saved = await api.saveRender({ url: bounceResult.outputUrl, fileName: bounceResult.fileName });
          if (saved.canceled) {
            setStatus("Bounce save cancelled");
            return;
          }
          if (!saved.ok) throw new Error(saved.error || "Bounce could not be saved");
          setStatus(`Bounce saved · ${saved.filePath || bounceResult.fileName}`);
          return;
        }
      }

      const response = await fetch(bounceResult.outputUrl, { headers: bridgeHeaders() });
      if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = bounceResult.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setStatus(`Bounce downloaded · ${bounceResult.fileName}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Bounce save failed";
      setBounceError(message);
      setStatus(`Bounce save failed · ${message}`);
    } finally {
      setIsSavingBounce(false);
    }
  }

  async function importAudioMedia(file: File) {
    if (!selectedTrack || selectedTrack.type !== "audio") {
      setStatus("Select an audio track before importing media");
      return;
    }
    if (engineState !== "connected") {
      setStatus("Start the local Mac bridge before importing audio");
      return;
    }
    setStatus(`Preparing ${file.name}…`);
    try {
      const converted = await convertAudioToWave(file);
      const baseName = file.name.replace(/\.[^.]+$/, "") || "audio";
      const uploaded = await uploadWave(converted.blob, `${baseName}.wav`);
      const targetTrackId = selectedTrack.id;
      const clipStart = snapTimelinePosition(position);
      mutate((next) => {
        const track = next.tracks.find((item) => item.id === targetTrackId);
        if (!track) return;
        track.clips ??= [];
        const clip = { id: nextId("clip", track.clips.map((item) => item.id)), file: uploaded.file, start: clipStart, offset: 0, length: converted.duration, fadeIn: 0, fadeOut: 0 };
        track.clips.push(clip);
        setSelectedClipId(clip.id);
      }, `${uploaded.name} imported`);
      setStatus(`${uploaded.name} imported to ${selectedTrack.name} · JUCE media ready`);
    } catch (error) {
      setStatus(`Audio import failed · ${error instanceof Error ? error.message : "unsupported file"}`);
    }
  }

  async function refreshAudioInputs() {
    try {
      const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      permissionStream.getTracks().forEach((track) => track.stop());
      const devices = (await navigator.mediaDevices.enumerateDevices()).filter((device) => device.kind === "audioinput");
      setAudioInputs(devices);
      if (!audioInputId && devices[0]) setAudioInputId(devices[0].deviceId);
      setStatus(`${devices.length} audio input${devices.length === 1 ? "" : "s"} available`);
    } catch (error) {
      setStatus(`Audio input unavailable · ${error instanceof Error ? error.message : "permission denied"}`);
    }
  }

  function playCountIn() {
    const beats = countInBars * Math.max(1, Number(project.timesig.split("/")[0]) || 4);
    if (beats <= 0) return;
    const context = new AudioContext();
    const beatLength = 60 / project.tempo;
    for (let beat = 0; beat < beats; beat += 1) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = beat % Math.max(1, Number(project.timesig.split("/")[0]) || 4) === 0 ? 1320 : 880;
      gain.gain.setValueAtTime(0.0001, context.currentTime + beat * beatLength);
      gain.gain.exponentialRampToValueAtTime(0.2, context.currentTime + beat * beatLength + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + beat * beatLength + 0.07);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(context.currentTime + beat * beatLength);
      oscillator.stop(context.currentTime + beat * beatLength + 0.08);
    }
    window.setTimeout(() => { void context.close(); }, Math.ceil((beats * beatLength + 0.2) * 1000));
  }

  async function startPortalRecording() {
    if (!selectedTrack || selectedTrack.type !== "audio") {
      setStatus("Select an audio track before recording");
      return;
    }
    if (!selectedTrack.recordArmed) {
      setStatus(`Arm ${selectedTrack.name} with R before recording`);
      return;
    }
    if (engineState !== "connected") {
      setStatus("Start the local Mac bridge before recording");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioInputId ? { deviceId: { exact: audioInputId } } : true });
      recordingStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];
      recordingTrackRef.current = selectedTrack.id;
      recordingStartRef.current = punchEnabled ? safePunchIn : position;
      recorder.ondataavailable = (event) => { if (event.data.size > 0) recordingChunksRef.current.push(event.data); };
      recorder.onstop = () => {
        const take = new Blob(recordingChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        stream.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        mediaRecorderRef.current = null;
        setIsRecording(false);
        void (async () => {
          try {
            const converted = await convertAudioToWave(take);
            const stamp = new Date().toISOString().replace(/[:.]/g, "-");
            const uploaded = await uploadWave(converted.blob, `take-${stamp}.wav`);
            const trackId = recordingTrackRef.current;
            const clipStart = recordingStartRef.current;
            mutate((next) => {
              const track = next.tracks.find((item) => item.id === trackId);
              if (!track) return;
              track.clips ??= [];
              const clip = { id: nextId("clip", track.clips.map((item) => item.id)), file: uploaded.file, start: clipStart, offset: 0, length: converted.duration, fadeIn: 0.01, fadeOut: 0.01 };
              track.clips.push(clip);
              setSelectedClipId(clip.id);
            }, `${uploaded.name} recorded`);
            setStatus(`${uploaded.name} recorded · ${converted.duration.toFixed(1)} seconds`);
          } catch (error) {
            setStatus(`Recording could not be saved · ${error instanceof Error ? error.message : "conversion failed"}`);
          }
        })();
      };

      const countInSeconds = countInBars * secondsPerBar;
      setIsRecording(true);
      if (countInSeconds > 0) {
        playCountIn();
        setStatus(`Count-in · ${countInBars} bar${countInBars === 1 ? "" : "s"}`);
      }
      recordStartTimerRef.current = window.setTimeout(() => {
        recordStartTimerRef.current = null;
        recorder.start(200);
        void startNativePlayback({ from: recordingStartRef.current, metronome: isMetronomeEnabled, cycle: false });
        setStatus(`Recording ${selectedTrack.name}${punchEnabled ? ` · punch ${formatTime(safePunchIn)}–${formatTime(safePunchOut)}` : ""}`);
        if (punchEnabled) {
          recordStopTimerRef.current = window.setTimeout(() => { void stopPortalRecording(); }, Math.max(50, (safePunchOut - safePunchIn) * 1000));
        }
      }, countInSeconds * 1000);
    } catch (error) {
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      setIsRecording(false);
      setStatus(`Recording failed · ${error instanceof Error ? error.message : "audio input unavailable"}`);
    }
  }

  async function stopPortalRecording() {
    if (recordStartTimerRef.current !== null) {
      window.clearTimeout(recordStartTimerRef.current);
      recordStartTimerRef.current = null;
    }
    if (recordStopTimerRef.current !== null) {
      window.clearTimeout(recordStopTimerRef.current);
      recordStopTimerRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder?.state && recorder.state !== "inactive") recorder.stop();
    else {
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      mediaRecorderRef.current = null;
      setIsRecording(false);
      setStatus("Recording cancelled");
    }
    await stopNativePlayback();
  }

  async function togglePortalRecording() {
    if (isRecording) await stopPortalRecording();
    else await startPortalRecording();
  }

  function setTrackValue(trackId: string, key: "gain" | "pan" | "muted" | "solo" | "recordArmed" | "inputMonitoring" | "hidden", value: number | boolean) {
    mutate((next) => {
      const track = next.tracks.find((item) => item.id === trackId);
      if (!track) return;
      if (key === "gain") track.gain = Math.max(-60, Math.min(12, Number(value)));
      if (key === "pan") track.pan = Math.max(-1, Math.min(1, Number(value)));
      if (key === "muted") track.muted = Boolean(value);
      if (key === "solo") track.solo = Boolean(value);
      if (key === "recordArmed") track.recordArmed = Boolean(value);
      if (key === "inputMonitoring") track.inputMonitoring = Boolean(value);
      if (key === "hidden") track.hidden = Boolean(value);
    }, `${key} changed`, `track:${trackId}:${key}`);
  }

  function setTrackName(trackId: string, name: string) {
    mutate((next) => {
      const track = next.tracks.find((item) => item.id === trackId);
      if (track) track.name = name;
    }, "track renamed", `track:${trackId}:name`);
  }

  function setTrackColor(trackId: string, color: string) {
    mutate((next) => {
      const track = next.tracks.find((item) => item.id === trackId);
      if (track && /^#[0-9a-f]{6}$/i.test(color)) track.color = color;
    }, "track colour changed", `track:${trackId}:color`);
  }

  function setTrackAutomationMode(trackId: string, mode: AutomationMode) {
    mutate((next) => {
      const track = next.tracks.find((item) => item.id === trackId);
      if (track) track.automationMode = mode;
    }, `${mode} automation mode`, `track:${trackId}:automation-mode`);
  }

  function addTrack(type: TrackType) {
    mutate((next) => {
      const id = nextId("track", next.tracks.map((track) => track.id));
      next.tracks.push({ id, name: `${TRACK_LABELS[type]} ${next.tracks.length + 1}`, type, gain: 0, pan: 0, muted: false, solo: false, recordArmed: false, inputMonitoring: false, hidden: false, automationMode: "Read", clips: type === "audio" ? [] : undefined, midiClips: type === "midi" ? [] : undefined, pattern: type === "drum" ? { startBar: 1, grid: "1/16", lengthBars: 1, hits: [] } : undefined });
      setSelectedTrackId(id);
    }, `${TRACK_LABELS[type]} track added`);
  }

  function removeSelectedTrack() {
    if (!selectedTrack) return;
    if (project.tracks.length <= 1) {
      setStatus("A project must keep at least one track");
      return;
    }
    const removedId = selectedTrack.id;
    mutate((next) => {
      next.tracks = next.tracks.filter((track) => track.id !== removedId);
      next.plugins = next.plugins.filter((plugin) => plugin.track !== removedId);
      next.fx = next.fx.filter((fx) => fx.track !== removedId);
      next.sends = next.sends.filter((send) => send.from !== removedId && send.to !== removedId);
      setSelectedTrackId(next.tracks[0]?.id ?? "");
    }, `${selectedTrack.name} removed`);
  }

  function unhideAllTracks() {
    if (!project.tracks.some((track) => track.hidden)) return;
    mutate((next) => { next.tracks.forEach((track) => { track.hidden = false; }); }, "all tracks shown");
  }

  function setTrackBus(trackId: string, busId: string) {
    mutate((next) => {
      const track = next.tracks.find((item) => item.id === trackId);
      if (track) track.bus = busId || undefined;
    }, busId ? "channel output routed" : "channel routed to stereo output");
  }

  function addSend(trackId: string, to: string) {
    if (!to || project.sends.some((send) => send.from === trackId && send.to === to)) return;
    mutate((next) => {
      next.sends.push({ id: nextId("send", next.sends.map((send) => send.id)), from: trackId, to, amount: 0.25 });
    }, "aux send added");
    setSendDestination("");
  }

  function setSendAmount(sendId: string, amount: number) {
    mutate((next) => {
      const send = next.sends.find((item) => item.id === sendId);
      if (send) send.amount = Math.max(0, Math.min(1, amount));
    }, "send level changed", `send:${sendId}:amount`);
  }

  function removeSend(sendId: string) {
    mutate((next) => { next.sends = next.sends.filter((send) => send.id !== sendId); }, "aux send removed");
  }

  function addMarker() {
    const markerTime = snapTimelinePosition(position);
    mutate((next) => {
      const id = nextId("marker", next.markers.map((marker) => marker.id));
      next.markers.push({ id, name: `Marker ${next.markers.length + 1}`, time: markerTime });
      next.markers.sort((a, b) => a.time - b.time);
    }, `marker added at ${formatTime(markerTime)}`);
  }

  function removeMarker(markerId: string) {
    mutate((next) => { next.markers = next.markers.filter((marker) => marker.id !== markerId); }, "marker removed");
  }

  function renameMarker(marker: DawMarker) {
    const name = window.prompt("Marker name", marker.name)?.trim();
    if (!name || name === marker.name) return;
    mutate((next) => {
      const target = next.markers.find((item) => item.id === marker.id);
      if (target) target.name = name;
    }, "marker renamed");
  }

  function addArrangementSection() {
    const start = isCycleEnabled ? safeLoopStart : snapTimelinePosition(position);
    const length = isCycleEnabled ? safeLoopEnd - safeLoopStart : secondsPerBar * 4;
    mutate((next) => {
      const id = nextId("section", next.arrangement.map((item) => item.id));
      next.arrangement.push({ id, name: `Section ${next.arrangement.length + 1}`, start, length });
      next.arrangement.sort((a, b) => a.start - b.start);
    }, `arrangement section added at ${formatTime(start)}`);
  }

  function renameArrangementSection(section: DawArrangementSection) {
    const name = window.prompt("Arrangement section name", section.name)?.trim();
    if (!name || name === section.name) return;
    mutate((next) => {
      const target = next.arrangement.find((item) => item.id === section.id);
      if (target) target.name = name;
    }, "arrangement section renamed");
  }

  function removeArrangementSection(sectionId: string) {
    mutate((next) => { next.arrangement = next.arrangement.filter((item) => item.id !== sectionId); }, "arrangement section removed");
  }

  function addAutomationPoint(track: DawTrack, event: MouseEvent<HTMLDivElement>) {
    if (!showAutomation) return;
    event.preventDefault();
    event.stopPropagation();
    const bounds = event.currentTarget.getBoundingClientRect();
    const raw = Math.max(0, Math.min(duration, (event.clientX - bounds.left) / pixelsPerSecond));
    const time = snapTimelinePosition(raw);
    const value = automationParameter === "gain" ? track.gain : track.pan;
    mutate((next) => {
      let envelope = next.envelopes.find((item) => item.track === track.id && item.param === automationParameter);
      if (!envelope) {
        envelope = { id: nextId("env", next.envelopes.map((item) => item.id)), track: track.id, param: automationParameter, points: [] };
        next.envelopes.push(envelope);
      }
      const existing = envelope.points.find((point) => Math.abs(Number(point.time) - time) < 0.01);
      if (existing) existing.value = value;
      else envelope.points.push({ time: time.toFixed(3), value });
      envelope.points.sort((a, b) => Number(a.time) - Number(b.time));
    }, `${automationParameter} automation point added`);
  }

  function deleteSelectedClip() {
    if (!selectedTrack || !selectedClip) return;
    mutate((next) => {
      const track = next.tracks.find((item) => item.id === selectedTrack.id);
      if (track?.clips) track.clips = track.clips.filter((clip) => clip.id !== selectedClip.id);
    }, `${selectedClip.file} deleted`);
    setSelectedClipId("");
  }

  function duplicateSelectedClip() {
    if (!selectedTrack || !selectedClip) return;
    mutate((next) => {
      const track = next.tracks.find((item) => item.id === selectedTrack.id);
      if (!track?.clips) return;
      const duplicate = { ...selectedClip, id: nextId("clip", track.clips.map((clip) => clip.id)), start: selectedClip.start + (selectedClip.length || 4) };
      track.clips.push(duplicate);
      setSelectedClipId(duplicate.id);
    }, `${selectedClip.file} duplicated`);
  }

  function updateSelectedClip(key: "start" | "offset" | "length" | "fadeIn" | "fadeOut", value: number) {
    if (!selectedTrack || !selectedClip) return;
    mutate((next) => {
      const clip = next.tracks.find((track) => track.id === selectedTrack.id)?.clips?.find((item) => item.id === selectedClip.id);
      if (!clip) return;
      if (key === "start") clip.start = Math.max(0, value);
      if (key === "offset") clip.offset = Math.max(0, value);
      if (key === "length") clip.length = Math.max(0.05, value);
      if (key === "fadeIn") clip.fadeIn = Math.max(0, Math.min(clip.length || 0.05, value));
      if (key === "fadeOut") clip.fadeOut = Math.max(0, Math.min(clip.length || 0.05, value));
    }, `region ${key} changed`, `clip:${selectedClip.id}:${key}`);
  }

  function splitSelectedClip() {
    if (!selectedTrack || !selectedClip) return;
    const splitAt = snapTimelinePosition(position);
    const clipEnd = selectedClip.start + (selectedClip.length || 4);
    if (splitAt <= selectedClip.start + 0.01 || splitAt >= clipEnd - 0.01) {
      setStatus("Place the playhead inside the selected region to split it");
      return;
    }
    mutate((next) => {
      const track = next.tracks.find((item) => item.id === selectedTrack.id);
      const clip = track?.clips?.find((item) => item.id === selectedClip.id);
      if (!track?.clips || !clip) return;
      const leftLength = splitAt - clip.start;
      const rightLength = clipEnd - splitAt;
      const right = {
        ...clip,
        id: nextId("clip", track.clips.map((item) => item.id)),
        start: splitAt,
        offset: (clip.offset || 0) + leftLength,
        length: rightLength,
        fadeIn: 0,
      };
      clip.length = leftLength;
      clip.fadeOut = 0;
      track.clips.push(right);
      setSelectedClipId(right.id);
    }, `${selectedClip.file} split at ${formatTime(splitAt)}`);
  }

  function beginClipDrag(event: ReactPointerEvent<HTMLElement>, trackId: string, clip: DawClip, mode: "move" | "start" | "end" = "move") {
    event.preventDefault();
    event.stopPropagation();
    rememberSnapshot(project, `clip:${clip.id}:drag:${Date.now()}`);
    clipDragRef.current = { trackId, clipId: clip.id, mode, startX: event.clientX, originalStart: clip.start, originalLength: clip.length || 4, originalOffset: clip.offset || 0 };
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelectedTrackId(trackId);
    setSelectedClipId(clip.id);
  }

  function moveClipDrag(event: ReactPointerEvent<HTMLElement>) {
    const drag = clipDragRef.current;
    if (!drag) return;
    event.preventDefault();
    const delta = (event.clientX - drag.startX) / pixelsPerSecond;
    const nextStart = Math.max(0, snapTimelinePosition(drag.originalStart + delta));
    const nextEnd = Math.max(drag.originalStart + 0.05, snapTimelinePosition(drag.originalStart + drag.originalLength + delta));
    setProject((current) => {
      const next = cloneProject(current);
      const clip = next.tracks.find((track) => track.id === drag.trackId)?.clips?.find((item) => item.id === drag.clipId);
      if (clip && drag.mode === "move") clip.start = nextStart;
      if (clip && drag.mode === "start") {
        const end = drag.originalStart + drag.originalLength;
        const start = Math.min(end - 0.05, nextStart);
        const trimmed = start - drag.originalStart;
        clip.start = start;
        clip.offset = Math.max(0, drag.originalOffset + trimmed);
        clip.length = Math.max(0.05, end - start);
        clip.fadeIn = Math.min(clip.fadeIn || 0, clip.length);
      }
      if (clip && drag.mode === "end") {
        // Snap the dragged edge in timeline space, then derive the region
        // length from its fixed start. Snapping the length itself makes the
        // right edge jump to the wrong grid position and can appear stuck.
        clip.length = Math.max(0.05, nextEnd - drag.originalStart);
        clip.fadeOut = Math.min(clip.fadeOut || 0, clip.length);
      }
      return next;
    });
    setIsDirty(true);
    setStatus(drag.mode === "move" ? `Region moved to ${formatTime(nextStart)}` : drag.mode === "end" ? `Region shortened to ${formatTime(nextEnd - drag.originalStart)}` : "Region resized");
  }

  function beginAutomationDrag(event: ReactPointerEvent<HTMLButtonElement>, envelopeId: string, pointIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    rememberSnapshot(project, `automation:${envelopeId}:${pointIndex}:${Date.now()}`);
    automationDragRef.current = { envelopeId, pointIndex };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function moveAutomationPoint(event: ReactPointerEvent<HTMLButtonElement>) {
    const drag = automationDragRef.current;
    if (!drag) return;
    const lane = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!lane) return;
    const time = snapTimelinePosition(Math.max(0, Math.min(duration, (event.clientX - lane.left) / pixelsPerSecond)));
    const ratio = Math.max(0, Math.min(1, (event.clientY - lane.top) / lane.height));
    const value = automationParameter === "gain" ? 12 - ratio * 72 : 1 - ratio * 2;
    setProject((current) => {
      const next = cloneProject(current);
      const point = next.envelopes.find((item) => item.id === drag.envelopeId)?.points[drag.pointIndex];
      if (point) {
        point.time = time.toFixed(3);
        point.value = Number(value.toFixed(3));
      }
      return next;
    });
    setIsDirty(true);
    setStatus(`${automationParameter} automation edited`);
  }

  function endAutomationDrag(event: ReactPointerEvent<HTMLButtonElement>) {
    automationDragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function removeAutomationPoint(envelopeId: string, pointIndex: number) {
    mutate((next) => {
      const envelope = next.envelopes.find((item) => item.id === envelopeId);
      if (envelope) envelope.points.splice(pointIndex, 1);
      next.envelopes = next.envelopes.filter((item) => item.points.length > 0);
    }, "automation point removed");
  }

  function endClipDrag(event: ReactPointerEvent<HTMLElement>) {
    clipDragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function addClip() {
    if (!selectedTrack || selectedTrack.type !== "audio") {
      setStatus("Select an audio track before adding a clip");
      return;
    }
    mutate((next) => {
      const track = next.tracks.find((item) => item.id === selectedTrack.id);
      if (!track) return;
      track.clips ??= [];
      track.clips.push({ id: nextId("clip", track.clips.map((clip) => clip.id)), file: "New audio file.wav", start: Math.round(position * 4) / 4, length: 4 });
    }, "audio clip placeholder added");
  }

  async function startNativePlayback(options: { from?: number; metronome?: boolean; cycle?: boolean } = {}) {
    if (!BRIDGE_URL) {
      setStatus("Mac JUCE bridge not configured · set VITE_MINI_DAW_BRIDGE_URL");
      return;
    }
    let bridgeReady = engineState === "connected";
    if (!bridgeReady) {
      setStatus("Checking the local Mac JUCE bridge…");
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 2500);
      try {
        const health = await fetch(`${BRIDGE_URL}/health`, { signal: controller.signal, headers: bridgeHeaders(), cache: "no-store" });
        const payload = await health.json().catch(() => null) as { ok?: boolean } | null;
        if (!health.ok || !payload?.ok) throw new Error(`HTTP ${health.status}`);
        setEngineState("connected");
        bridgeReady = true;
      } catch {
        setEngineState("offline");
        setStatus("Play unavailable · start the local Mini-DAW bridge at 127.0.0.1:8787");
      } finally {
        window.clearTimeout(timeout);
      }
    }
    if (!bridgeReady) return;
    const cycleEnabled = options.cycle ?? isCycleEnabled;
    const metronomeEnabled = options.metronome ?? isMetronomeEnabled;
    const candidateFrom = options.from ?? position;
    const requestedFrom = candidateFrom >= duration ? 0 : candidateFrom;
    const from = cycleEnabled && (requestedFrom < safeLoopStart || requestedFrom >= safeLoopEnd) ? safeLoopStart : requestedFrom;
    setPosition(from);
    setIsPreparingPlayback(true);
    try {
      const engineProject = await prepareProjectForEngine(project);
      setStatus("Starting Mac JUCE playback…");
      const response = await fetch(`${BRIDGE_URL}/transport/play`, {
        method: "POST",
        headers: bridgeHeaders(true),
        body: JSON.stringify({
          project: engineProject,
          from,
          duration,
          metronome: metronomeEnabled,
          loopStart: cycleEnabled ? safeLoopStart : undefined,
          loopEnd: cycleEnabled ? safeLoopEnd : undefined,
        }),
      });
      const result = await response.json() as { ok?: boolean; message?: string; transport?: NativeTransport };
      if (!response.ok || !result.ok || !result.transport) throw new Error(result.message || `HTTP ${response.status}`);
      setIsPlaying(result.transport.state === "playing");
      setPosition(Math.max(0, Math.min(duration, result.transport.position)));
      setStatus(`Playing through the Mac JUCE engine${cycleEnabled ? " · cycle on" : ""}${metronomeEnabled ? " · click on" : ""}`);
    } catch (error) {
      setIsPlaying(false);
      const message = error instanceof Error ? error.message : "bridge unavailable";
      if (!message.startsWith("Drive media:")) setEngineState("offline");
      setStatus(message.startsWith("Drive media:") ? message : `JUCE playback failed · ${message}`);
    } finally {
      setIsPreparingPlayback(false);
    }
  }

  async function stopNativePlayback() {
    if (!BRIDGE_URL) {
      setIsPlaying(false);
      setPosition(0);
      setStatus("Playback stopped");
      return;
    }
    setStatus("Stopping Mac JUCE playback…");
    try {
      const response = await fetch(`${BRIDGE_URL}/transport/stop`, { method: "POST", headers: bridgeHeaders(true) });
      const result = await response.json() as { ok?: boolean; message?: string; transport?: NativeTransport };
      if (!response.ok || !result.ok || !result.transport) throw new Error(result.message || `HTTP ${response.status}`);
      setIsPlaying(false);
      setPosition(Math.max(0, Math.min(duration, result.transport.position)));
      setMasterMeter(0);
      setTrackMeters([]);
      setStatus("Playback stopped");
    } catch (error) {
      setIsPlaying(false);
      setEngineState("offline");
      setStatus(`JUCE stop failed · ${error instanceof Error ? error.message : "bridge unavailable"}`);
    }
  }

  async function updateLiveTransport(next: { metronome?: boolean; cycle?: boolean }) {
    const resumeAt = position;
    const nextMetronome = next.metronome ?? isMetronomeEnabled;
    const nextCycle = next.cycle ?? isCycleEnabled;
    if (next.metronome !== undefined) setIsMetronomeEnabled(next.metronome);
    if (next.cycle !== undefined) setIsCycleEnabled(next.cycle);
    if (!isPlaying) return;
    await stopNativePlayback();
    await startNativePlayback({ from: resumeAt, metronome: nextMetronome, cycle: nextCycle });
  }

  async function returnToStart() {
    if (isPlaying) await stopNativePlayback();
    else setPosition(0);
    setPosition(0);
    setStatus("Playhead returned to the start");
  }

  function nudgePosition(amount: number) {
    const next = Math.max(0, Math.min(duration, position + amount));
    setPosition(next);
    setStatus(`Playhead moved to ${formatTime(next)}`);
  }

  async function scanPlugins() {
    if (!BRIDGE_URL) {
      setPluginScanStatus("Set the Mac bridge URL first");
      return;
    }
    setIsScanningPlugins(true);
    setPluginScanStatus("Scanning installed AU/VST3 bundles…");
    try {
      const response = await fetch(`${BRIDGE_URL}/plugins`, { headers: bridgeHeaders() });
      const result = await response.json() as { ok?: boolean; message?: string; plugins?: InstalledPlugin[]; count?: number; cached?: boolean };
      if (!response.ok || !result.ok) throw new Error(result.message || `HTTP ${response.status}`);
      const plugins = Array.isArray(result.plugins) ? result.plugins : [];
      setAvailablePlugins(plugins);
      setPluginScanStatus(`${result.count ?? plugins.length} Mac plugins found${result.cached ? " · cached" : ""}`);
      setStatus("Mac plugin registry ready · choose a native plugin to add it");
    } catch (error) {
      setPluginScanStatus(error instanceof Error ? error.message : "Plugin scan failed");
      setStatus("Mac plugin scan failed · native processing is still available for saved paths");
    } finally {
      setIsScanningPlugins(false);
    }
  }

  function selectInstalledPlugin(selection: string) {
    setNewPluginSelection(selection);
    const installed = availablePlugins.find((plugin) => `${plugin.format}|${plugin.path}` === selection);
    if (!installed) return;
    setNewPluginName(installed.descriptiveName || installed.name);
    setNewPluginFormat(installed.format.toLowerCase().includes("audio") ? "AU" : installed.format === "AU" ? "AU" : "VST3");
  }

  async function addPluginToTrack(trackId: string, selection: string, manualName = "", manualFormat: "AU" | "VST3" = "VST3") {
    const targetTrack = project.tracks.find((track) => track.id === trackId);
    if (!targetTrack) {
      setStatus("Select a track for a plugin");
      return;
    }
    const installed = availablePlugins.find((plugin) => `${plugin.format}|${plugin.path}` === selection);
    const name = (installed?.descriptiveName || installed?.name || manualName).trim();
    if (!name) {
      setStatus("Enter a plugin name first");
      return;
    }
    const pluginId = nextId("plugin", project.plugins.map((plugin) => plugin.id));
    mutate((next) => {
      next.plugins.push({ id: pluginId, track: trackId, name, format: installed?.format.toLowerCase().includes("audio") || installed?.format === "AU" ? "AU" : manualFormat, path: installed?.path, params: {}, enabled: true });
    }, `${name} added to ${targetTrack.name}`);
    if (installed && BRIDGE_URL) {
      setStatus(`${name} added · loading native parameter metadata…`);
      try {
        const info = await requestPluginInfo(installed.format, installed.path);
        setPluginInfo((current) => ({ ...current, [pluginId]: info }));
        setStatus(`${name} added · ${info.parameterCount} native parameters ready`);
      } catch (error) {
        setStatus(`${name} added · native controls unavailable (${error instanceof Error ? error.message : "inspection failed"})`);
      }
    }
  }

  async function addPlugin() {
    if (!selectedTrack) return;
    await addPluginToTrack(selectedTrack.id, newPluginSelection, newPluginName, newPluginFormat);
    setNewPluginName("");
    setNewPluginSelection("");
  }

  function movePlugin(pluginId: string, direction: -1 | 1) {
    mutate((next) => {
      const index = next.plugins.findIndex((plugin) => plugin.id === pluginId);
      if (index < 0) return;
      const plugin = next.plugins[index];
      const sameTrack = next.plugins.map((item, itemIndex) => item.track === plugin.track ? itemIndex : -1).filter((itemIndex) => itemIndex >= 0);
      const position = sameTrack.indexOf(index);
      const targetIndex = sameTrack[position + direction];
      if (targetIndex === undefined) return;
      [next.plugins[index], next.plugins[targetIndex]] = [next.plugins[targetIndex], next.plugins[index]];
    }, direction < 0 ? "insert moved up" : "insert moved down");
  }

  function changePlugin(pluginId: string, parameter: string, value: ParamValue) {
    mutate((next) => {
      const plugin = next.plugins.find((item) => item.id === pluginId);
      if (plugin) plugin.params[parameter] = value;
    }, "plugin parameter changed");
  }

  return (
    <div className={`daw-page daw-theme-${theme}`} style={{ fontFamily }}>
      <section className="content-panel daw-workspace-panel">
        <div className="daw-console-toolbar">
          <div className="daw-console-top">
            <div className="daw-console-left">
              <span className="daw-console-zone-label">VIEW</span>
              <div className="daw-console-side-tools" aria-label="DAW views">
                <div className="daw-view-group">
                  <button aria-label="Toggle channel strip" aria-pressed={viewState.inserts} className={`daw-view-toggle ${viewState.inserts ? "active" : ""}`} onClick={() => toggleViewSection("inserts")} title="Show or hide channel strip" type="button"><PanelLeft className="h-4 w-4" /></button>
                  <button aria-label="Toggle mixer" aria-pressed={viewState.mixer} className={`daw-view-toggle ${viewState.mixer ? "active" : ""}`} onClick={() => toggleViewSection("mixer")} title="Show or hide mixer" type="button"><SlidersVertical className="h-4 w-4" /></button>
                  <button aria-label="Toggle arrangement" aria-pressed={viewState.arrangement} className={`daw-view-toggle ${viewState.arrangement ? "active" : ""}`} onClick={() => toggleViewSection("arrangement")} title="Show or hide arrangement" type="button"><AudioWaveform className="h-4 w-4" /></button>
                </div>
                <span className="daw-toolbar-divider" />
                <div className="daw-history-group">
                  <button aria-label="Undo" className="daw-console-icon" disabled={undoDepth === 0} onClick={undo} title="Undo (Command-Z)" type="button"><Undo2 className="h-4 w-4" /></button>
                  <button aria-label="Redo" className="daw-console-icon" disabled={redoDepth === 0} onClick={redo} title="Redo (Shift-Command-Z)" type="button"><Redo2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>

            <div className="daw-console-center">
              <div className="daw-project-title-block daw-project-title-center">
                <input aria-label="Project name" className="daw-console-project-name" onBlur={(event) => { if (!event.currentTarget.value.trim()) mutate((next) => { next.name = "Untitled project"; }, "project name reset"); }} onChange={(event) => mutate((next) => { next.name = event.target.value; }, "project name changed", "project:name")} value={project.name} />
              </div>
              <div className="daw-console-center-row">
              <div className="daw-transport-deck">
                <span className="daw-transport-label">TRANSPORT</span>
                <div className="daw-transport-line">
                  <div className="daw-console-right daw-project-actions daw-transport-project-actions">
                  </div>
                  <div className="daw-transport-cluster" aria-label="Transport">
                  <button aria-label="Return to start" className="daw-console-icon" onClick={() => void returnToStart()} title="Return to start" type="button"><SkipBack className="h-4 w-4" /></button>
                  <button aria-label="Stop" className="daw-console-icon daw-transport-stop" disabled={!isPlaying && position <= 0} onClick={() => void stopNativePlayback()} title="Stop" type="button"><Square className="h-3.5 w-3.5" /></button>
                  <button aria-busy={isPreparingPlayback} aria-label={isPreparingPlayback ? "Preparing playback" : isPlaying ? "Pause" : "Play"} className={`daw-transport-play ${isPlaying ? "active" : ""}`} disabled={isPreparingPlayback} onClick={() => void (isPlaying ? stopNativePlayback() : startNativePlayback())} title={engineState === "connected" ? (isPreparingPlayback ? "Preparing playback" : isPlaying ? "Pause Mac engine playback" : "Play through the Mac engine") : "Connect the local Mac engine to play"} type="button">{isPreparingPlayback ? <RefreshCw className="h-4 w-4 spinning" /> : isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</button>
                  <button aria-label={isRecording ? "Stop recording" : "Record"} aria-pressed={isRecording} className={`daw-console-icon daw-record-button ${isRecording ? "active recording" : ""}`} onClick={() => void togglePortalRecording()} title={isRecording ? "Stop recording" : "Record the armed audio track"} type="button">{isRecording ? <Square className="h-3.5 w-3.5" /> : <Circle className="h-4 w-4" />}</button>
                  <button aria-label="Nudge forward" className="daw-console-icon" onClick={() => nudgePosition(4)} title="Nudge forward" type="button"><SkipForward className="h-4 w-4" /></button>
                  <span className="daw-transport-divider" />
                  <button aria-label="Cycle" aria-pressed={isCycleEnabled} className={`daw-console-icon ${isCycleEnabled ? "active" : ""}`} onClick={() => void updateLiveTransport({ cycle: !isCycleEnabled })} title="Cycle" type="button"><Repeat2 className="h-4 w-4" /></button>
                  <button aria-label="Metronome" aria-pressed={isMetronomeEnabled} className={`daw-console-icon daw-click-toggle ${isMetronomeEnabled ? "active" : ""}`} onClick={() => void updateLiveTransport({ metronome: !isMetronomeEnabled })} title="Metronome click" type="button"><TimerReset className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>

              <div className="daw-lcd" aria-label="Transport display">
                <div className="daw-lcd-position"><small>BAR</small><strong>{String(currentBar).padStart(3, "0")}</strong><span>:</span><strong>{String(currentBeat).padStart(2, "0")}</strong><small>BEAT</small></div>
                <div className="daw-lcd-fields">
                  <label><small>TEMPO</small><input aria-label="Tempo" max={300} min={20} onChange={(event) => mutate((next) => { next.tempo = Math.max(20, Math.min(300, Number(event.target.value) || 120)); }, "tempo changed")} step={0.1} type="number" value={project.tempo} /></label>
                  <label><small>KEY</small><input aria-label="Key" maxLength={8} onChange={(event) => mutate((next) => { next.key = event.target.value.toUpperCase().slice(0, 8) || "C"; }, "key changed")} value={project.key} /></label>
                  <label><small>METER</small><input aria-label="Time signature" maxLength={8} onChange={(event) => mutate((next) => { next.timesig = event.target.value || "4/4"; }, "time signature changed")} value={project.timesig} /></label>
                  <span><small>TIME</small><strong>{formatTime(position)}</strong></span>
                </div>
              </div>
              </div>
            </div>

            <div className="daw-console-project">
              <div className="daw-console-right daw-project-actions">
                <div className="daw-appearance-menu">
                  <button
                    aria-expanded={showAppearanceSettings}
                    aria-haspopup="menu"
                    aria-label="Mini DAW menu"
                    className={`daw-console-icon daw-appearance-trigger ${showAppearanceSettings ? "active" : ""}`}
                    onClick={() => {
                      setShowProjectMenu(false);
                      setShowRecordSettings(false);
                      setShowBounceSettings(false);
                      setShowAppearanceSettings((current) => !current);
                    }}
                    title="Menu"
                    type="button"
                  >
                    <Menu className="h-4 w-4" />
                  </button>
                  {showAppearanceSettings ? (
                    <div aria-label="Mini DAW menu" className="daw-appearance-popover" role="menu">
                      <div className="daw-menu-project-head"><strong>{project.name}</strong>{lastSavedAt ? <small>Last saved {formatLastSaved(lastSavedAt)}</small> : <small>Not saved yet</small>}</div>
                      <div className="daw-menu-group">
                        <div className="daw-menu-group-title">Project</div>
                        <button className="daw-menu-item" onClick={() => { setShowAppearanceSettings(false); saveProject(); }} role="menuitem" type="button"><Save className="h-3.5 w-3.5" /> Save project</button>
                        <button className="daw-menu-item" onClick={() => { setShowAppearanceSettings(false); resetProject(); }} role="menuitem" type="button"><Plus className="h-3.5 w-3.5" /> New project</button>
                        <button className="daw-menu-item" onClick={() => { setShowAppearanceSettings(false); mediaInput.current?.click(); }} role="menuitem" type="button"><FolderOpen className="h-3.5 w-3.5" /> Import audio…</button>
                        <button className="daw-menu-item" onClick={() => { if (desktop) void openDesktopProject(); else { setShowAppearanceSettings(false); fileInput.current?.click(); } }} role="menuitem" type="button"><Upload className="h-3.5 w-3.5" /> {desktop ? "Open project JSON…" : "Open local JSON…"}</button>
                        <button className="daw-menu-item" onClick={() => { setShowAppearanceSettings(false); downloadProject(); }} role="menuitem" type="button"><Download className="h-3.5 w-3.5" /> Export project</button>
                        <button className="daw-menu-item" onClick={() => { setShowAppearanceSettings(false); void renderBounce(); }} disabled={isRendering || engineState !== "connected"} role="menuitem" type="button">{isRendering ? <RefreshCw className="h-3.5 w-3.5 spinning" /> : <Download className="h-3.5 w-3.5" />} Bounce to WAV</button>
                      </div>
                      <div className="daw-menu-divider" />
                      <AppearanceSettings />
                    </div>
                  ) : null}
                </div>
                <input accept="audio/*,.wav,.aif,.aiff,.flac,.mp3,.m4a" className="sr-only" onChange={(event) => { const file = event.target.files?.[0]; if (file) void importAudioMedia(file); event.target.value = ""; }} ref={mediaInput} type="file" />
                {!desktop ? <input accept="application/json,.json" className="sr-only" onChange={(event) => { const file = event.target.files?.[0]; if (file) importProject(file); event.target.value = ""; }} ref={fileInput} type="file" /> : null}
              </div>
            </div>
          </div>

          <div className="daw-edit-toolbar">
            <span className="daw-edit-toolbar-label">Edit</span>
            <div aria-label="Editing behavior" className="daw-toolbar-group daw-toolbar-edit-group">
              <span className="daw-toolbar-group-label">Edit</span>
              <div className="daw-edit-select-group">
                <label>Snap <select aria-label="Snap mode" onChange={(event) => setSnapMode(event.target.value as SnapMode)} value={snapMode}><option>Smart</option><option>Bar</option><option>Beat</option><option>Off</option></select></label>
                <label>Drag <select aria-label="Drag mode" onChange={(event) => setDragMode(event.target.value as DragMode)} value={dragMode}><option>No Overlap</option><option>Overlap</option></select></label>
              </div>
            </div>
            {viewState.arrangement ? <div aria-label="Track and clip actions" className="daw-toolbar-group daw-toolbar-track-group">
              <span className="daw-toolbar-group-label">Track</span>
              <div className="daw-arrangement-actions">
                <select aria-label="Add track" className="daw-add-track-select" onChange={(event) => { const type = event.target.value as TrackType; if (type) addTrack(type); }} value=""><option value="">+ Add track</option><option value="audio">Audio</option><option value="midi">MIDI</option><option value="drum">Drums</option><option value="bus">Bus</option></select>
                <button className="daw-secondary-button compact" onClick={addClip} type="button"><Plus className="h-3.5 w-3.5" /> Clip</button>
                {project.tracks.some((track) => track.hidden) ? <button className="daw-secondary-button compact" onClick={unhideAllTracks} type="button">Show hidden</button> : null}
                <button aria-label="Remove selected track" className="daw-icon-button compact danger" onClick={removeSelectedTrack} title="Remove selected track" type="button"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div> : null}
            <div aria-label="Timeline tools" className="daw-toolbar-group daw-toolbar-tools-group">
              <span className="daw-toolbar-group-label">Timeline</span>
              <div className="daw-edit-action-group">
                <button aria-expanded={showEditTools} className={`daw-secondary-button compact ${showEditTools ? "active" : ""}`} onClick={() => setShowEditTools((current) => !current)} type="button"><SlidersVertical className="h-3.5 w-3.5" /> Tools</button>
              {showEditTools ? <>
                <button aria-pressed={showMarkers} className={`daw-secondary-button compact ${showMarkers ? "active" : ""}`} onClick={() => setShowMarkers((current) => !current)} type="button"><MapPin className="h-3.5 w-3.5" /> Markers</button>
                <button aria-label="Add marker at playhead" className="daw-icon-button compact" onClick={addMarker} title="Add marker at playhead" type="button"><Plus className="h-3.5 w-3.5" /></button>
                <button className="daw-secondary-button compact" onClick={addArrangementSection} title="Add arrangement section at the playhead or cycle range" type="button"><Plus className="h-3.5 w-3.5" /> Section</button>
                <button aria-pressed={showAutomation} className={`daw-secondary-button compact ${showAutomation ? "active" : ""}`} onClick={() => setShowAutomation((current) => !current)} type="button"><Route className="h-3.5 w-3.5" /> Automation</button>
                {showAutomation ? <select aria-label="Automation parameter" onChange={(event) => setAutomationParameter(event.target.value as "gain" | "pan")} value={automationParameter}><option value="gain">Volume</option><option value="pan">Pan</option></select> : null}
              </> : null}
              </div>
            </div>
            {isCycleEnabled ? <div aria-label="Cycle range" className="daw-toolbar-group daw-toolbar-range-group">
              <div className="daw-cycle-range"><span>Cycle</span><label>From<input aria-label="Cycle start" max={duration} min={0} onChange={(event) => setLoopStart(Math.max(0, Number(event.target.value)))} step={0.1} type="number" value={safeLoopStart.toFixed(1)} /></label><label>To<input aria-label="Cycle end" max={duration} min={0.05} onChange={(event) => setLoopEnd(Math.max(0.05, Number(event.target.value)))} step={0.1} type="number" value={safeLoopEnd.toFixed(1)} /></label></div>
            </div> : null}
            <div aria-label="Recording tools" className="daw-toolbar-group daw-toolbar-record-group">
              <span className="daw-toolbar-group-label">Record</span>
              <div className="daw-record-settings">
                <button aria-expanded={showRecordSettings} aria-haspopup="true" aria-label="Recording settings" className={`daw-secondary-button compact ${showRecordSettings ? "active" : ""}`} onClick={() => { setShowAppearanceSettings(false); setShowBounceSettings(false); const next = !showRecordSettings; setShowRecordSettings(next); if (next && audioInputs.length === 0) void refreshAudioInputs(); }} type="button"><Mic className="h-3.5 w-3.5" /> Recording</button>
                {showRecordSettings ? <div className="daw-record-settings-popover">
                  <div className="daw-record-settings-help"><strong>Pre-roll (count-in)</strong><p>Play this many bars before recording begins.</p></div>
                  <label>Pre-roll<select aria-label="Pre-roll bars" onChange={(event) => { setCountInBars(Number(event.target.value)); setShowRecordSettings(false); }} value={countInBars}><option value={0}>Off</option><option value={1}>1 bar</option><option value={2}>2 bars</option></select></label>
                  <div className="daw-record-settings-help"><strong>Punch range</strong><p>Record only between the In and Out times.</p></div>
                  <label className="daw-record-punch-toggle"><button aria-pressed={punchEnabled} className={`daw-secondary-button compact ${punchEnabled ? "active" : ""}`} onClick={() => setPunchEnabled((current) => !current)} type="button"><TimerReset className="h-3.5 w-3.5" /> Punch recording</button>{punchEnabled ? <span className="daw-punch-range"><input aria-label="Punch in" max={duration} min={0} onChange={(event) => setPunchIn(Number(event.target.value))} step={0.1} type="number" value={safePunchIn.toFixed(1)} /><span>In–Out</span><input aria-label="Punch out" max={duration} min={0.05} onChange={(event) => setPunchOut(Number(event.target.value))} step={0.1} type="number" value={safePunchOut.toFixed(1)} /></span> : null}</label>
                  <div className="daw-record-settings-help"><strong>Audio input</strong><p>The Mac or interface source recorded when you press Record.</p></div>
                  <label className="daw-record-input"><button aria-label="Refresh audio inputs" className="daw-icon-button compact" onClick={() => void refreshAudioInputs()} title="Refresh available inputs" type="button"><RefreshCw className="h-3.5 w-3.5" /></button><select aria-label="Audio input" disabled={audioInputs.length === 0} onChange={(event) => { setAudioInputId(event.target.value); setShowRecordSettings(false); }} value={audioInputId}><option value="">Default input</option>{audioInputs.map((device, index) => <option key={device.deviceId || index} value={device.deviceId}>{device.label || `Input ${index + 1}`}</option>)}</select></label>
                </div> : null}
              </div>
            </div>
            {viewState.arrangement ? <div className="daw-unified-arrangement-controls">
              <div className="daw-toolbar-group daw-toolbar-view-group">
                <span className="daw-toolbar-group-label">View</span>
                <div className="daw-view-controls" aria-label="Timeline view controls">
                  <div className="daw-zoom-control" aria-label="Timeline zoom">
                    <button aria-label="Zoom timeline out" className="daw-icon-button compact" onClick={() => setManualTimelineZoom(timelineZoom - 8)} title="Zoom out" type="button"><ZoomOut className="h-3.5 w-3.5" /></button>
                    <input aria-label="Timeline zoom" className="daw-zoom-range" max={140} min={4} onChange={(event) => setManualTimelineZoom(Number(event.target.value))} step={1} type="range" value={timelineZoom} />
                    <button aria-label="Zoom timeline in" className="daw-icon-button compact" onClick={() => setManualTimelineZoom(timelineZoom + 8)} title="Zoom in" type="button"><ZoomIn className="h-3.5 w-3.5" /></button>
                    <span className="daw-zoom-readout">{Math.round((timelineZoom / 52) * 100)}%</span>
                  </div>
                </div>
              </div>
              <div className="daw-fit-controls" aria-label="Fit arrangement to window">
                <button aria-label="Fit timeline horizontally" className={`daw-icon-button compact ${fitHorizontalActive ? "active" : ""}`} onClick={fitTimelineHorizontal} title="Fit timeline horizontally" type="button"><StretchHorizontal className="h-3.5 w-3.5" /></button>
                <button aria-label="Fit tracks vertically" className={`daw-icon-button compact ${fitVerticalActive ? "active" : ""}`} onClick={fitTimelineVertical} title="Fit tracks vertically" type="button"><StretchVertical className="h-3.5 w-3.5" /></button>
                <button aria-label="Fit tracks horizontally and vertically" className={`daw-icon-button compact ${fitHorizontalActive && fitVerticalActive ? "active" : ""}`} onClick={fitTimelineToWindow} title="Fit tracks horizontally and vertically" type="button"><Maximize2 className="h-3.5 w-3.5" /></button>
              </div>
            </div> : null}
          </div>
        </div>

        <div className={`daw-main-workspace ${viewState.inserts ? "has-inspector" : ""} ${viewState.arrangement ? "has-arrangement" : ""} ${viewState.mixer ? "has-mixer" : ""}`}>
          {viewState.inserts && <aside className="daw-inspector-panel">
            <div className="daw-inspector-heading">
              <div><span className="eyebrow">Inspector</span><h2>Channel strip</h2></div>
              <button aria-label="Hide channel strip" className="daw-console-icon" onClick={() => toggleViewSection("inserts")} title="Hide channel strip" type="button"><PanelLeft className="h-4 w-4" /></button>
            </div>
            {selectedTrack ? <div className="daw-inspector-scroll">
              <div className="daw-inspector-track">
                <DawTrackColorPicker onChange={(event) => setTrackColor(selectedTrack.id, event.target.value)} track={selectedTrack} />
                <div className="daw-inspector-track-copy">
                  <input aria-label="Track name" className="daw-inspector-track-name" onBlur={(event) => { if (!event.currentTarget.value.trim()) setTrackName(selectedTrack.id, "Untitled track"); }} onChange={(event) => setTrackName(selectedTrack.id, event.target.value)} value={selectedTrack.name} />
                  <small>{selectedTrack.gain.toFixed(1)} dB · {selectedTrack.pan === 0 ? "C" : selectedTrack.pan < 0 ? `L${Math.round(Math.abs(selectedTrack.pan) * 100)}` : `R${Math.round(selectedTrack.pan * 100)}`}</small>
                </div>
              </div>
              <div className="daw-inspector-route">
                <label><span>Input</span>{selectedTrack.type === "audio" ? <select aria-label="Channel input" onChange={(event) => setAudioInputId(event.target.value)} onFocus={() => { if (audioInputs.length === 0) void refreshAudioInputs(); }} value={audioInputId}><option value="">Default input</option>{audioInputs.map((device, index) => <option key={device.deviceId || index} value={device.deviceId}>{device.label || `Input ${index + 1}`}</option>)}</select> : <select aria-label="Channel input" disabled value="Instrument"><option>Instrument</option></select>}</label>
                <label><span>Output</span><select aria-label="Channel output" onChange={(event) => setTrackBus(selectedTrack.id, event.target.value)} value={selectedTrack.bus || ""}><option value="">Stereo Out</option>{busTracks.filter((track) => track.id !== selectedTrack.id).map((track) => <option key={track.id} value={track.id}>{track.name}</option>)}</select></label>
                <label><span>Auto</span><select aria-label="Automation mode" onChange={(event) => setTrackAutomationMode(selectedTrack.id, event.target.value as AutomationMode)} value={selectedTrack.automationMode || "Read"}><option>Read</option><option>Touch</option><option>Latch</option><option>Write</option></select></label>
              </div>
              <div className="daw-inspector-ms"><button className={selectedTrack.recordArmed ? "active record" : ""} onClick={() => setTrackValue(selectedTrack.id, "recordArmed", !selectedTrack.recordArmed)} type="button">R Arm</button>{selectedTrack.type === "audio" ? <button className={selectedTrack.inputMonitoring ? "active input" : ""} onClick={() => setTrackValue(selectedTrack.id, "inputMonitoring", !selectedTrack.inputMonitoring)} type="button">I Input</button> : null}<button className={selectedTrack.muted ? "active mute" : ""} onClick={() => setTrackValue(selectedTrack.id, "muted", !selectedTrack.muted)} type="button">M Mute</button><button className={selectedTrack.solo ? "active solo" : ""} onClick={() => setTrackValue(selectedTrack.id, "solo", !selectedTrack.solo)} type="button">S Solo</button></div>

              <section className="daw-inspector-section">
                <div className="daw-inspector-section-heading"><strong>Inserts</strong><span>{selectedPlugins.length}</span></div>
                <PluginInsertChain
                  availablePlugins={availablePlugins}
                  onInsert={(trackId, selection) => {
                    void addPluginToTrack(trackId, selection);
                    setInsertSelectionByTrack((current) => ({ ...current, [trackId]: "" }));
                  }}
                  onSelectTrack={setSelectedTrackId}
                  onSelectionChange={(selection) => setInsertSelectionByTrack((current) => ({ ...current, [selectedTrack.id]: selection }))}
                  plugins={selectedPlugins}
                  selection={insertSelectionByTrack[selectedTrack.id] ?? ""}
                  track={selectedTrack}
                />
              </section>

              {selectedTrack.type !== "bus" ? <section className="daw-inspector-section">
                <div className="daw-inspector-section-heading"><strong>Sends</strong><span>{selectedSends.length}</span></div>
                <div className="daw-send-list">
                  {selectedSends.map((send) => {
                    const destination = busTracks.find((track) => track.id === send.to);
                    return <div className="daw-send-row" key={send.id}><span>{destination?.name || send.to}</span><input aria-label={`Send to ${destination?.name || send.to}`} max={1} min={0} onChange={(event) => setSendAmount(send.id, Number(event.target.value))} step={0.01} type="range" value={send.amount} /><output>{Math.round(send.amount * 100)}%</output><button aria-label="Remove send" className="daw-icon-button compact danger" onClick={() => removeSend(send.id)} type="button"><Trash2 className="h-3 w-3" /></button></div>;
                  })}
                  {busTracks.length > 0 ? <div className="daw-send-add"><select aria-label="Send destination" onChange={(event) => setSendDestination(event.target.value)} value={sendDestination}><option value="">Add send…</option>{busTracks.filter((track) => !selectedSends.some((send) => send.to === track.id)).map((track) => <option key={track.id} value={track.id}>{track.name}</option>)}</select><button aria-label="Add send" disabled={!sendDestination} onClick={() => addSend(selectedTrack.id, sendDestination)} type="button"><Plus className="h-3.5 w-3.5" /></button></div> : <span className="daw-inspector-helper">Add a bus track to create a send.</span>}
                </div>
              </section> : null}

              {selectedClip ? <section className="daw-inspector-section daw-region-inspector">
                <div className="daw-inspector-section-heading"><strong>Region</strong><span>{selectedClip.file}</span></div>
                <div className="daw-region-fields"><label>Start<input min={0} onChange={(event) => updateSelectedClip("start", Number(event.target.value))} step={0.01} type="number" value={selectedClip.start} /></label><label>Length<input min={0.05} onChange={(event) => updateSelectedClip("length", Number(event.target.value))} step={0.01} type="number" value={selectedClip.length || 4} /></label><label>Offset<input min={0} onChange={(event) => updateSelectedClip("offset", Number(event.target.value))} step={0.01} type="number" value={selectedClip.offset || 0} /></label><label>Fade in<input max={selectedClip.length || 4} min={0} onChange={(event) => updateSelectedClip("fadeIn", Number(event.target.value))} step={0.01} type="number" value={selectedClip.fadeIn || 0} /></label><label>Fade out<input max={selectedClip.length || 4} min={0} onChange={(event) => updateSelectedClip("fadeOut", Number(event.target.value))} step={0.01} type="number" value={selectedClip.fadeOut || 0} /></label></div>
                <div className="daw-region-actions"><button className="daw-secondary-button compact" onClick={splitSelectedClip} type="button"><Scissors className="h-3.5 w-3.5" /> Split</button><button className="daw-secondary-button compact" onClick={duplicateSelectedClip} type="button">Duplicate</button><button className="daw-secondary-button compact danger" onClick={deleteSelectedClip} type="button">Delete</button></div>
              </section> : null}

              <section className="daw-inspector-fader-section">
                <div className="daw-inspector-fader-head"><span>Pan</span><output>{formatPan(selectedTrack.pan)}</output></div>
                <DawPanKnob className="inspector" label={`${selectedTrack.name} inspector pan`} onChange={(value) => setTrackValue(selectedTrack.id, "pan", value)} value={selectedTrack.pan} />
                <div className="daw-inspector-fader-row"><div aria-label={`${selectedTrack.name} level`} aria-valuemax={1} aria-valuemin={0} aria-valuenow={trackMeters[project.tracks.findIndex((track) => track.id === selectedTrack.id)] || 0} className="daw-inspector-meter" role="meter"><i style={{ height: `${Math.round((trackMeters[project.tracks.findIndex((track) => track.id === selectedTrack.id)] || 0) * 100)}%` }} /></div><DawFader className="inspector" label={`${selectedTrack.name} inspector gain`} maximum={12} minimum={-60} onChange={(value) => setTrackValue(selectedTrack.id, "gain", value)} step={0.1} value={selectedTrack.gain} /><output>{selectedTrack.gain.toFixed(1)} dB</output></div>
              </section>

              <section className="daw-inspector-section daw-native-controls">
                <div className="daw-inspector-section-heading"><strong>Native controls</strong><button className="daw-secondary-button compact" disabled={isScanningPlugins || engineState !== "connected"} onClick={() => void scanPlugins()} type="button"><AudioWaveform className="h-3.5 w-3.5" /> {isScanningPlugins ? "Scanning" : "Scan"}</button></div>
                <span className="daw-inspector-helper">{pluginScanStatus || (engineState === "connected" ? "AU/VST3 controls from the Mac registry." : "Connect the Mac bridge to inspect plugins.")}</span>
                <div className="daw-plugin-add-row compact"><select aria-label="Installed Mac plugin" className="daw-plugin-installed-select" onChange={(event) => selectInstalledPlugin(event.target.value)} value={newPluginSelection}><option value="">Choose insert…</option>{availablePlugins.map((plugin) => <option key={`${plugin.format}|${plugin.path}`} value={`${plugin.format}|${plugin.path}`}>{plugin.name} · {plugin.format}</option>)}</select><input aria-label="Manual plugin name" onChange={(event) => setNewPluginName(event.target.value)} placeholder="Manual plugin" value={newPluginName} /><select aria-label="Plugin format" onChange={(event) => setNewPluginFormat(event.target.value as "AU" | "VST3")} value={newPluginFormat}><option value="VST3">VST3</option><option value="AU">AU</option></select><button className="daw-secondary-button compact" onClick={() => void addPlugin()} type="button"><Plus className="h-3.5 w-3.5" /> Add</button></div>
                {selectedPlugins.length > 0 ? selectedPlugins.map((plugin, index) => <PluginCard canMoveDown={index < selectedPlugins.length - 1} canMoveUp={index > 0} info={pluginInfo[plugin.id]} key={plugin.id} onChange={(parameter, value) => changePlugin(plugin.id, parameter, value)} onMove={(direction) => movePlugin(plugin.id, direction)} onRemove={() => { setPluginInfo((current) => { const next = { ...current }; delete next[plugin.id]; return next; }); mutate((next) => { next.plugins = next.plugins.filter((item) => item.id !== plugin.id); }, `${plugin.name} removed`); }} onToggle={() => mutate((next) => { const current = next.plugins.find((item) => item.id === plugin.id); if (current) current.enabled = !current.enabled; }, `${plugin.name} bypass changed`)} plugin={plugin} />) : <div className="daw-empty-plugin compact"><AudioWaveform className="h-5 w-5" /><strong>No inserts on this channel</strong><p>Scan the Mac registry or add a manual AU/VST3 entry.</p></div>}
              </section>
            </div> : <div className="daw-empty-plugin"><AudioWaveform className="h-6 w-6" /><strong>No selected track</strong><p>Select a track in the arrangement or mixer.</p></div>}
          </aside>}

          <div className={`daw-main-column ${viewState.arrangement ? "has-arrangement" : ""} ${viewState.mixer ? "has-mixer" : ""} ${viewState.arrangement && viewState.mixer ? "resizable-mixer" : ""}`} style={viewState.arrangement && viewState.mixer ? { gridTemplateRows: `minmax(0, 1fr) 8px ${mixerHeight}px` } : undefined}>
            {viewState.arrangement && <section className="daw-arrangement-panel">
              <div aria-label="Scrollable timeline. Use the mouse wheel to scroll; hold Shift for horizontal scrolling." className={`daw-timeline-scroll ${fitTracks ? "fit-tracks" : ""} ${trackRowHeight < 64 ? "compact-tracks" : ""}`} ref={timelineScrollRef} role="region">
                <div className="daw-timeline-canvas" style={{ "--daw-track-label-width": `${trackLabelWidth}px`, width: `${timelineWidth}px` } as CSSProperties}>
                  <div className="daw-timeline-header">
                    <div className="daw-timeline-label">TRACKS<button aria-label={`Resize track controls bar, ${trackLabelWidth} pixels`} className={`daw-track-label-resize-handle ${isResizingTrackLabel ? "active" : ""}`} onClick={(event) => event.stopPropagation()} onKeyDown={resizeTrackLabelByKeyboard} onPointerDown={beginTrackLabelResize} title="Drag to resize the track controls bar" type="button"><span aria-hidden="true" /></button></div>
                    <div className="daw-ruler" onClick={seekTimeline} style={{ left: `${leftWidth}px`, width: `${timelineWidth - leftWidth}px` }}>
                      {isCycleEnabled ? <i className="daw-cycle-ruler" style={{ left: `${safeLoopStart * pixelsPerSecond}px`, width: `${Math.max(3, (safeLoopEnd - safeLoopStart) * pixelsPerSecond)}px` }} /> : null}
                      {punchEnabled ? <i className="daw-punch-ruler" style={{ left: `${safePunchIn * pixelsPerSecond}px`, width: `${Math.max(3, (safePunchOut - safePunchIn) * pixelsPerSecond)}px` }} /> : null}
                      {(() => {
                        const barPx = secondsPerBar * pixelsPerSecond;
                        const step = Math.max(1, Math.ceil(28 / Math.max(1, barPx)));
                        const totalBars = Math.ceil(duration / secondsPerBar) + 1;
                        return Array.from({ length: totalBars }, (_, index) => index % step === 0 ? <span key={index} style={{ left: `${index * secondsPerBar * pixelsPerSecond}px` }}>{index + 1}</span> : null);
                      })()}
                    </div>
                  </div>
                  {project.arrangement.length > 0 ? <div className="daw-global-marker-row daw-arrangement-row">
                    <div className="daw-global-marker-label"><AudioWaveform className="h-3.5 w-3.5" /> Arrangement</div>
                    <div className="daw-global-marker-lane" style={{ left: `${leftWidth}px`, width: `${timelineWidth - leftWidth}px` }}>
                      {project.arrangement.map((section) => <button className="daw-arrangement-section" key={section.id} onClick={(event) => { event.stopPropagation(); setPosition(section.start); }} onContextMenu={(event) => { event.preventDefault(); removeArrangementSection(section.id); }} onDoubleClick={(event) => { event.stopPropagation(); renameArrangementSection(section); }} style={{ left: `${section.start * pixelsPerSecond}px`, width: `${Math.max(42, section.length * pixelsPerSecond)}px` }} title={`${section.name} · double-click to rename · right-click to remove`} type="button">{section.name}</button>)}
                    </div>
                  </div> : null}
                  {showMarkers ? <div className="daw-global-marker-row">
                    <div className="daw-global-marker-label"><MapPin className="h-3.5 w-3.5" /> Markers</div>
                    <div className="daw-global-marker-lane" style={{ left: `${leftWidth}px`, width: `${timelineWidth - leftWidth}px` }}>
                      {project.markers.map((marker) => <button className="daw-marker" key={marker.id} onClick={(event) => { event.stopPropagation(); setPosition(marker.time); }} onContextMenu={(event) => { event.preventDefault(); removeMarker(marker.id); }} onDoubleClick={(event) => { event.stopPropagation(); renameMarker(marker); }} style={{ left: `${marker.time * pixelsPerSecond}px` }} title={`${marker.name} · ${formatTime(marker.time)} · double-click to rename · right-click to remove`} type="button"><span>{marker.name}</span></button>)}
                    </div>
                  </div> : null}
                  {visibleTracks.map((track, visibleIndex) => {
                    const color = trackColor(track);
                    const gainPercentage = Math.round(((Math.max(-60, Math.min(12, track.gain)) + 60) / 72) * 100);
                    return (
                    <div className={`daw-track-row ${selectedTrack?.id === track.id ? "selected" : ""}`} key={track.id} onClick={() => setSelectedTrackId(track.id)} onContextMenu={(event) => { event.preventDefault(); event.stopPropagation(); setSelectedTrackId(track.id); setColorMenu({ trackId: track.id, x: event.clientX, y: event.clientY }); }} style={{ height: `${trackRowHeight}px` }}>
                      <div className="daw-track-label">
                        <button aria-label={`Resize track controls bar, ${trackLabelWidth} pixels`} className={`daw-track-label-resize-handle ${isResizingTrackLabel ? "active" : ""}`} onClick={(event) => event.stopPropagation()} onKeyDown={resizeTrackLabelByKeyboard} onPointerDown={beginTrackLabelResize} title="Drag to resize the track controls bar" type="button"><span aria-hidden="true" /></button>
                        <div className="daw-track-identity">
                          <span className="daw-track-glyph" style={{ borderColor: `${color}99`, color }}><DawTrackGlyph track={track} /></span>
                          <div className="daw-track-label-main">
                          <input aria-label={`Track name for ${track.name || "untitled track"}`} className="daw-track-name-input" onChange={(event) => setTrackName(track.id, event.target.value)} onClick={(event) => event.stopPropagation()} onFocus={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()} onBlur={(event) => { if (!event.currentTarget.value.trim()) setTrackName(track.id, "Untitled track"); }} value={track.name} />
                            <div className="daw-track-label-buttons">
                              <button aria-label={`Mute ${track.name}`} className={track.muted ? "active mute" : ""} onClick={(event) => { event.stopPropagation(); setTrackValue(track.id, "muted", !track.muted); }} title="Mute" type="button">M</button>
                              <button aria-label={`Solo ${track.name}`} className={track.solo ? "active solo" : ""} onClick={(event) => { event.stopPropagation(); setTrackValue(track.id, "solo", !track.solo); }} title="Solo" type="button">S</button>
                              <button aria-label={`Arm ${track.name}`} className={track.recordArmed ? "active record" : ""} onClick={(event) => { event.stopPropagation(); setTrackValue(track.id, "recordArmed", !track.recordArmed); }} title="Record enable" type="button">R</button>
                              {track.type === "audio" ? <button aria-label={`Monitor ${track.name}`} className={track.inputMonitoring ? "active input" : ""} onClick={(event) => { event.stopPropagation(); setTrackValue(track.id, "inputMonitoring", !track.inputMonitoring); }} title="Input monitoring" type="button">I</button> : null}
                              <button aria-label={`Hide ${track.name}`} className="track-hide-button" onClick={(event) => { event.stopPropagation(); setTrackValue(track.id, "hidden", true); }} title="Hide track" type="button">H</button>
                            </div>
                            {showAutomation ? <select aria-label={`${track.name} automation mode`} className="daw-track-automation-mode" onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()} onChange={(event) => setTrackAutomationMode(track.id, event.target.value as AutomationMode)} value={track.automationMode || "Read"}><option>Read</option><option>Touch</option><option>Latch</option><option>Write</option></select> : null}
                          </div>
                        </div>
                        <label className="daw-track-volume-control" title={`${track.name} level ${track.gain.toFixed(1)} dB`} onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()}>
                          <input aria-label={`${track.name} level`} max={12} min={-60} onChange={(event) => setTrackValue(track.id, "gain", Number(event.target.value))} step={0.1} style={{ background: `linear-gradient(to right, ${color} 0 ${gainPercentage}%, #343638 ${gainPercentage}% 100%)` }} type="range" value={track.gain} />
                        </label>
                        <div className="daw-track-pan-control" title={`${track.name} pan ${formatPan(track.pan)}`}><DawPanKnob className="arrangement" label={`${track.name} pan`} onChange={(value) => setTrackValue(track.id, "pan", value)} value={track.pan} /></div>
                      </div>
                      <div className={`daw-track-lane ${showAutomation ? "show-automation" : ""}`} onClick={seekTimeline} onDoubleClick={(event) => addAutomationPoint(track, event)} style={{ left: `${leftWidth}px`, width: `${timelineWidth - leftWidth}px` }}>
                        <span aria-hidden="true" className="daw-lane-playhead" style={{ left: `${position * pixelsPerSecond}px` }} />
                        {(track.clips ?? []).map((clip) => <button className={`daw-clip daw-audio-clip ${selectedClipId === clip.id ? "selected" : ""}`} key={clip.id} onClick={(event) => { event.stopPropagation(); setSelectedTrackId(track.id); setSelectedClipId(clip.id); }} onPointerDown={(event) => beginClipDrag(event, track.id, clip)} onPointerMove={moveClipDrag} onPointerUp={endClipDrag} onPointerCancel={endClipDrag} style={{ borderLeftColor: trackColor(track), left: `${clip.start * pixelsPerSecond}px`, width: `${Math.max(18, (clip.length || 4) * pixelsPerSecond)}px` }} title={`${clip.file} · drag to move · drag the right edge to shorten or extend`} type="button"><b aria-label={`Trim start of ${clip.file}`} className="daw-clip-resize start" onPointerDown={(event) => beginClipDrag(event, track.id, clip, "start")} /><span>{clip.file}</span><DawClipWaveform color={trackColor(track)} file={clip.file} waveform={waveforms[normalizeMediaPath(clip.file).toLocaleLowerCase()]} /><b aria-label={`Trim end of ${clip.file}`} className="daw-clip-resize end" onPointerDown={(event) => beginClipDrag(event, track.id, clip, "end")} title="Drag to shorten or extend this region" />{(clip.fadeIn || 0) > 0 ? <em className="daw-clip-fade in" style={{ width: `${Math.min(100, ((clip.fadeIn || 0) / (clip.length || 4)) * 100)}%` }} /> : null}{(clip.fadeOut || 0) > 0 ? <em className="daw-clip-fade out" style={{ width: `${Math.min(100, ((clip.fadeOut || 0) / (clip.length || 4)) * 100)}%` }} /> : null}</button>)}
                        {(track.pattern?.hits ?? []).map((hit, index) => { const hitSeconds = ((track.pattern?.startBar ?? 1) - 1 + hit.bar - 1) * secondsPerBar + (hit.beat - 1) * (60 / project.tempo); return <span className="daw-hit" key={`${track.id}-hit-${index}`} style={{ left: `${hitSeconds * pixelsPerSecond}px` }} />; })}
                        {(track.midiClips ?? []).flatMap((clip) => clip.notes.map((note, index) => <span className="daw-midi-note" key={`${clip.id}-note-${index}`} style={{ left: `${(clip.start + note.start * (60 / project.tempo)) * pixelsPerSecond}px`, width: `${Math.max(10, note.duration * (60 / project.tempo) * pixelsPerSecond)}px`, top: `${8 + (127 - note.note) % 28}px` }} />))}
                        {showAutomation ? project.envelopes.filter((envelope) => envelope.track === track.id && envelope.param === automationParameter).flatMap((envelope) => envelope.points.map((point, index) => <button aria-label={`${automationParameter} automation at ${point.time} seconds`} className="daw-automation-point" key={`${envelope.id}-${index}`} onContextMenu={(event) => { event.preventDefault(); removeAutomationPoint(envelope.id, index); }} onPointerDown={(event) => beginAutomationDrag(event, envelope.id, index)} onPointerMove={moveAutomationPoint} onPointerUp={endAutomationDrag} style={{ left: `${Number(point.time) * pixelsPerSecond}px`, top: `${automationParameter === "gain" ? Math.max(7, Math.min(trackRowHeight - 7, trackRowHeight - ((point.value + 60) / 72) * trackRowHeight)) : Math.max(7, Math.min(trackRowHeight - 7, ((1 - point.value) / 2) * trackRowHeight))}px` }} title={`${automationParameter}: ${point.value.toFixed(2)} at ${formatTime(Number(point.time))} · drag to edit · right-click to remove`} type="button" />)) : null}
                      </div>
                    </div>
                  );
                  })}
                  <div className="daw-playhead" onPointerDown={(event) => { event.preventDefault(); event.stopPropagation(); setPlayheadDragging(true); const el = event.currentTarget; el.setPointerCapture(event.pointerId); const move = (e: PointerEvent) => { const bounds = el.parentElement?.getBoundingClientRect(); if (bounds) { const raw = (e.clientX - bounds.left - leftWidth) / pixelsPerSecond; setPosition(Math.max(0, Math.min(duration, raw))); } }; const up = () => { setPlayheadDragging(false); el.removeEventListener("pointermove", move); el.removeEventListener("pointerup", up); el.removeEventListener("pointercancel", up); }; el.addEventListener("pointermove", move); el.addEventListener("pointerup", up); el.addEventListener("pointercancel", up); }} style={{ left: `${leftWidth + position * pixelsPerSecond}px` }}><span /></div>
                </div>
              </div>
            </section>}

            {viewState.arrangement && viewState.mixer ? <button aria-label="Resize mixer height" aria-orientation="horizontal" aria-valuemax={MAX_MIXER_HEIGHT} aria-valuemin={MIN_MIXER_HEIGHT} aria-valuenow={mixerHeight} className={`daw-mixer-resize-handle ${isResizingMixer ? "active" : ""}`} onKeyDown={resizeMixerByKeyboard} onPointerDown={beginMixerResize} role="separator" tabIndex={0} title="Drag to resize mixer; use arrow keys for fine adjustment" type="button"><span aria-hidden="true" /></button> : null}

            {viewState.mixer && <section className="daw-mixer-dock">
              <div className="daw-mixer-heading"><div className="daw-panel-title"><span className="eyebrow">Mix</span><strong>Mixer</strong></div><div className="daw-mixer-mode"><button className={mixerMode === "tracks" ? "active" : ""} onClick={() => setMixerMode("tracks")} type="button">Tracks</button><button className={mixerMode === "all" ? "active" : ""} onClick={() => setMixerMode("all")} type="button">All</button></div><SlidersVertical className="daw-section-icon" /></div>
              <div className="daw-mixer-scroll">
                {mixerTracks.map((track) => {
                  const trackPluginCount = project.plugins.filter((plugin) => plugin.track === track.id).length;
                  const trackSendCount = project.sends.filter((send) => send.from === track.id).length;
                  const outputTrack = track.bus ? project.tracks.find((item) => item.id === track.bus) : undefined;
                  const mixerInput = track.type === "audio" ? "Audio In" : track.type === "drum" ? "Drum Kit" : track.type === "midi" ? "MIDI In" : "Bus In";
                  const mixerOutput = outputTrack?.name || "Stereo Out";
                  const trackPlugins = project.plugins.filter((plugin) => plugin.track === track.id);
                  const trackSends = project.sends.filter((send) => send.from === track.id);
                  return <div className={`daw-mixer-strip ${selectedTrack?.id === track.id ? "selected" : ""}`} key={track.id} onClick={() => setSelectedTrackId(track.id)}>
                    <div className="daw-mixer-name" title={track.name}><DawTrackColorPicker className="mixer" onChange={(event) => setTrackColor(track.id, event.target.value)} track={track} /><input aria-label={`Mixer track name for ${track.name || "untitled track"}`} className="daw-mixer-name-input" onChange={(event) => setTrackName(track.id, event.target.value)} onClick={(event) => event.stopPropagation()} onFocus={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()} onBlur={(event) => { if (!event.currentTarget.value.trim()) setTrackName(track.id, "Untitled track"); }} value={track.name} /></div>
                    <div className="daw-mixer-route" onClick={(event) => event.stopPropagation()}>
                      <label><span>Input</span>{track.type === "audio" ? <select aria-label={`${track.name} input`} onChange={(event) => setAudioInputId(event.target.value)} onFocus={() => { if (audioInputs.length === 0) void refreshAudioInputs(); }} value={audioInputId}><option value="">Default input</option>{audioInputs.map((device, index) => <option key={device.deviceId || index} value={device.deviceId}>{device.label || `Input ${index + 1}`}</option>)}</select> : <select aria-label={`${track.name} input`} disabled value={mixerInput}><option>{mixerInput}</option></select>}</label>
                      <label><span>Output</span><select aria-label={`${track.name} output`} onChange={(event) => setTrackBus(track.id, event.target.value)} value={track.bus || ""}><option value="">{mixerOutput}</option>{busTracks.filter((bus) => bus.id !== track.id).map((bus) => <option key={bus.id} value={bus.id}>{bus.name}</option>)}</select></label>
                      <label><span>Auto</span><select aria-label={`${track.name} automation`} onChange={(event) => setTrackAutomationMode(track.id, event.target.value as AutomationMode)} value={track.automationMode || "Read"}><option>Read</option><option>Touch</option><option>Latch</option><option>Write</option></select></label>
                    </div>
                    <div className="daw-mixer-ms daw-inspector-ms" onClick={(event) => event.stopPropagation()}>
                      <button className={track.recordArmed ? "active record" : ""} onClick={() => setTrackValue(track.id, "recordArmed", !track.recordArmed)} type="button">R</button>
                      {track.type === "audio" ? <button className={track.inputMonitoring ? "active input" : ""} onClick={() => setTrackValue(track.id, "inputMonitoring", !track.inputMonitoring)} type="button">I</button> : null}
                      <button className={track.muted ? "active mute" : ""} onClick={() => setTrackValue(track.id, "muted", !track.muted)} type="button">M</button>
                      <button className={track.solo ? "active solo" : ""} onClick={() => setTrackValue(track.id, "solo", !track.solo)} type="button">S</button>
                    </div>
                    <div className="daw-mixer-inserts" onClick={(event) => event.stopPropagation()}>
                      <div className="daw-mixer-section-label"><span>Inserts</span><b>{trackPluginCount}</b></div>
                      <PluginInsertChain
                        availablePlugins={availablePlugins}
                        onInsert={(trackId, selection) => {
                          void addPluginToTrack(trackId, selection);
                          setInsertSelectionByTrack((current) => ({ ...current, [trackId]: "" }));
                        }}
                        onSelectTrack={setSelectedTrackId}
                        onSelectionChange={(selection) => setInsertSelectionByTrack((current) => ({ ...current, [track.id]: selection }))}
                        plugins={trackPlugins}
                        selection={insertSelectionByTrack[track.id] ?? ""}
                        track={track}
                      />
                    </div>
                    {track.type !== "bus" ? <div className="daw-mixer-sends-full" onClick={(event) => event.stopPropagation()}>
                      <div className="daw-mixer-section-label"><span>Sends</span><b>{trackSendCount}</b></div>
                      {trackSends.map((send) => {
                        const destination = busTracks.find((bus) => bus.id === send.to);
                        return <div className="daw-mixer-send" key={send.id}><span>{destination?.name || send.to}</span><input aria-label={`${track.name} send to ${destination?.name || send.to}`} max={1} min={0} onChange={(event) => setSendAmount(send.id, Number(event.target.value))} step={0.01} type="range" value={send.amount} /><output>{Math.round(send.amount * 100)}%</output><button aria-label={`Remove ${track.name} send`} onClick={() => removeSend(send.id)} type="button"><Trash2 className="h-3 w-3" /></button></div>;
                      })}
                      {busTracks.length > 0 ? <select aria-label={`Add send to ${track.name}`} onChange={(event) => { const destination = event.target.value; if (destination) addSend(track.id, destination); event.currentTarget.value = ""; }} value=""><option value="">+ Add send</option>{busTracks.filter((bus) => !trackSends.some((send) => send.to === bus.id)).map((bus) => <option key={bus.id} value={bus.id}>{bus.name}</option>)}</select> : null}
                    </div> : null}
                    <div className="daw-mixer-routing-summary" aria-label={`${track.name} processing summary`}>
                      <span>IN <b>{mixerInput}</b></span>
                      <span>OUT <b>{mixerOutput}</b></span>
                    </div>
                    <div className="daw-mixer-level" title={`${track.name} volume fader`}><div aria-label={`${track.name} level`} aria-valuemax={1} aria-valuemin={0} aria-valuenow={trackMeters[project.tracks.findIndex((item) => item.id === track.id)] || 0} className="daw-meter" role="meter"><i style={{ height: `${Math.round((trackMeters[project.tracks.findIndex((item) => item.id === track.id)] || 0) * 100)}%` }} /></div><DawFader label={`${track.name} gain`} maximum={12} minimum={-60} onChange={(value) => setTrackValue(track.id, "gain", value)} step={0.1} value={track.gain} /><output className="daw-fader-value">{track.gain.toFixed(1)} dB</output></div>
                    <DawPanKnob label={`${track.name} pan`} onChange={(value) => setTrackValue(track.id, "pan", value)} value={track.pan} />
                    <span className="daw-pan-label">{formatPan(track.pan)}</span>
                    <div className="daw-ms-buttons"><button className={track.muted ? "active mute" : ""} onClick={(event) => { event.stopPropagation(); setTrackValue(track.id, "muted", !track.muted); }} type="button">M</button><button className={track.solo ? "active solo" : ""} onClick={(event) => { event.stopPropagation(); setTrackValue(track.id, "solo", !track.solo); }} type="button">S</button></div>
                  </div>;
                })}
                <div className="daw-mixer-strip master"><div className="daw-mixer-name"><span style={{ background: "#f0c96a" }} />Master</div><div className="daw-mixer-signal-path" aria-label="Master signal path"><div><span>IN</span><b>All Tracks</b></div><div><span>OUT</span><b>Monitor</b></div></div><div className="daw-mixer-routing-summary" aria-label="Master processing summary"><span>LIMIT <b>{project.master.limiter ? "ON" : "OFF"}</b></span></div><div className="daw-mixer-level" title="Master volume fader"><div aria-label="Master level" aria-valuemax={1} aria-valuemin={0} aria-valuenow={masterMeter} className="daw-meter" role="meter"><i style={{ height: `${Math.round(masterMeter * 100)}%` }} /></div><DawFader label="Master gain" maximum={12} minimum={-60} onChange={(value) => mutate((next) => { next.master.gain = value; }, "master gain changed", "master:gain")} step={0.1} value={project.master.gain} /><output className="daw-fader-value">{project.master.gain.toFixed(1)} dB</output></div><button className={`daw-master-limit ${project.master.limiter ? "active" : ""}`} onClick={() => mutate((next) => { next.master.limiter = !next.master.limiter; }, "master limiter toggled")} type="button">Limiter {project.master.limiter ? "on" : "off"}</button></div>
              </div>
            </section>}

            {!viewState.arrangement && !viewState.mixer && <div className="daw-empty-workspace"><Gauge className="h-6 w-6" /><strong>Workspace hidden</strong><p>Use the view buttons above to show Arrangement or Mixer.</p></div>}
          </div>
        </div>

        {colorMenu ? <TrackColorMenu colorMenu={colorMenu} onClose={() => setColorMenu(null)} onPick={(color) => { setTrackColor(colorMenu.trackId, color); setColorMenu(null); }} /> : null}

        <section className="daw-footer-note"><FileJson className="h-4 w-4" /><span>{status}</span><span className="daw-footer-spacer" /><span>{project.tracks.length} tracks · {project.plugins.length} plugins · {project.envelopes.length} automation · {project.markers.length} markers</span><EngineBadge state={engineState} /></section>
      </section>
    </div>
  );
}
